// Clavian Boot's cloud contract (ADR-0063; spec/25 §13, §14; F-BOOT-01, -05, -06, -11, -12, -13, -14, -16, -19, -20, -23).
//
// The venue's boot server, its images and versions, its boot PCs, edits, wipes, rollouts and health — under
// `/v1/organizations/{o}/venues/{v}/boot/*`, each behind a `boot:*` permission **and** the organisation's `boot`
// entitlement (`402 entitlement-required` without it):
//
//   GET   servers                         boot:view     the venue's boot server, its status, disks and licence state
//   POST  servers/pairing-code            boot:manage   a one-time code to type into the boot server's first-run screen
//   PATCH servers/{id}                    boot:manage   rename it
//   POST  servers/{id}/unpair             boot:manage   revoke its device key; it must be paired again
//   POST  servers/{id}/update             boot:manage   install the server's own downloaded, verified update now (F-BOOT-23)
//   GET   images                          boot:view     system images and game disks, with their current version
//   POST  images                          boot:manage   a new image: a capture code, or an import on the boot server
//   PATCH images/{id}                     boot:manage   name, venue default, NIC drivers, game-disk assignment, archive
//   GET   images/{id}/versions            boot:view     every version: the restore points
//   POST  images/{id}/restore             boot:manage   make an earlier published version current again
//   POST  images/{id}/merge               boot:manage   merge old versions into a new base (a job on the boot server)
//   POST  images/{id}/prepare             boot:manage   prepare a failed version again, or a published captured or imported one as a new version (no new capture)
//   POST  images/{id}/edits               boot:edit_image  start an edit boot on one PC
//   POST  images/{id}/server-edits        boot:edit_image  open a game disk for updates on the boot server itself (a server edit)
//   POST  edits/{id}/publish              boot:edit_image  publish the edit as a new version
//   POST  edits/{id}/discard              boot:edit_image  throw the edit away
//   GET   pcs                             boot:view     every boot PC: station, image, version ("behind"), last boot
//   GET   floor                           boot:view     each PC linked to a station, with its boot state, for the Floor (F-BOOT-24)
//   POST  pcs                           boot:manage   add a PC by its MAC ahead of its first boot (licence-checked)
//   PATCH pcs/{id}                        boot:manage   station link, image, game disks, write-back override
//   POST  pcs/{id}/remove                 boot:manage   remove a PC: it no longer counts against the licence
//   POST  wipes                           boot:wipe     force-wipe write-back or shader caches
//   POST  pcs/{id}/shader-seed            boot:manage   make a warm PC's shader cache its GPU family's seed
//   POST  rollouts/restart-idle           boot:manage   restart idle PCs that are behind onto the current version
//   GET   metrics                         boot:view     per-minute (≤ 2 days) or daily health
//
// And the boot server's own two (spec/25 §13.1–§13.2):
//
//   POST /v1/boot-servers/pair     public, authorised by the one-time pairing code: binds the server's device key
//   GET  /v1/boot-server/config    device-token authenticated as kind `boot_server`: the signed boot config and lease
//
// The signed config document (`BootConfig`, `BootLicence`) and the boot server's LAN API (`BootLocalWhoami`,
// `BootLocalClientStatus`, the capture DTOs, the anti-cheat filter list, and the API's paths, capture headers and
// problem codes; spec/25 §9.1, §9.4, §13.4) are named components too, so the boot server's and `Clavian.Boot.Client`'s
// C# DTOs and names are generated from them (`station/Shared/Generated/Contracts.g.cs`), never written by hand.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { BootEditIdSchema, BootImageIdSchema, BootImageVersionIdSchema, BootPcIdSchema, BootServerIdSchema, BootShaderWipeIdSchema, DeviceIdSchema, MemberIdSchema, OrganizationIdSchema, StationClassIdSchema, StationCommandIdSchema, StationIdSchema, VenueIdSchema, ZoneIdSchema, } from "../ids/schema.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, jsonResponse, problemResponse } from "../openapi/responses.js";
import { BOOT_EDIT_IMAGE, BOOT_MANAGE, BOOT_VIEW, BOOT_WIPE, FLOOR_READ } from "../permissions/index.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { IdempotencyHeaderSchema } from "./id-checks.js";
import { MAX_STATION_NUMBER, StationSchema } from "./stations.js";
// ---------------------------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------------------------
/** A PC's MAC address as Clavian Boot writes it: six upper-case hex bytes, colon-separated (as `HardwareSummary`). */
export const BootMacSchema = z
    .string()
    .regex(/^([0-9A-F]{2}:){5}[0-9A-F]{2}$/)
    .openapi('BootMac', { description: 'A boot PC’s MAC address, upper-case and colon-separated.', example: '00:1A:2B:3C:4D:5E' });
/** A boot server's state (spec/25 §14): waiting to be paired, connected, or paired and not reporting. */
export const BOOT_SERVER_STATUSES = ['pairing', 'online', 'offline'];
export const BootServerStatusSchema = z.enum(BOOT_SERVER_STATUSES).openapi('BootServerStatus', {
    description: 'Whether a boot server is waiting to be paired, online, or paired and not reporting.',
});
/**
 * The licence lease's state as the boot server last reported it (spec/25 §13.3): `valid` until `validUntil`; `grace`
 * until `graceUntil` (known PCs boot, new ones are refused); `expired` after it (only the free allowance boots).
 */
export const BOOT_LICENCE_STATES = ['valid', 'grace', 'expired'];
export const BootLicenceStateSchema = z.enum(BOOT_LICENCE_STATES).openapi('BootLicenceState', {
    description: 'Where the boot server’s licence lease is: valid, in its 14-day grace, or expired.',
});
/** A bootable Windows system disk, or a shared game disk every PC sees as a second drive (spec/25 §1, §12). */
export const BOOT_IMAGE_KINDS = ['system', 'game'];
export const BootImageKindSchema = z.enum(BOOT_IMAGE_KINDS).openapi('BootImageKind', {
    description: 'A bootable system image, or a shared game disk.',
});
/** How an image's boot-start NIC drivers are chosen (spec/25 §9.3). */
export const BOOT_NIC_DRIVER_MODES = ['auto', 'families', 'broad'];
export const BootNicDriverModeSchema = z.enum(BOOT_NIC_DRIVER_MODES).openapi('BootNicDriverMode', {
    description: 'Which NIC driver families go into an image: the ones seen in the venue, the ones staff ticked, or every certified one.',
});
/** Where an image version is (spec/25 §14). Only `published` versions boot PCs and are restore points. */
export const BOOT_IMAGE_VERSION_STATES = ['capturing', 'preparing', 'editing', 'published', 'discarded', 'failed', 'merged'];
export const BootImageVersionStateSchema = z.enum(BOOT_IMAGE_VERSION_STATES).openapi('BootImageVersionState', {
    description: 'Where an image version is: being captured, prepared or edited; published; discarded; failed; or merged into a newer base.',
});
/** Where a PC's writes go during a boot (spec/25 §5.4, ADR-0063 decision 7). */
export const BOOT_WRITEBACK_POLICIES = ['auto', 'forceLocal', 'forceServer'];
export const BootWritebackPolicySchema = z.enum(BOOT_WRITEBACK_POLICIES).openapi('BootWritebackPolicy', {
    description: 'Where a PC’s writes go: its local NVMe when it has a scratch partition (auto), always local, or always the server.',
});
/** Where a boot's writes actually went. */
export const BOOT_WRITEBACK_LOCATIONS = ['local', 'server'];
export const BootWritebackLocationSchema = z.enum(BOOT_WRITEBACK_LOCATIONS).openapi('BootWritebackLocation', {
    description: 'Where a boot’s writes went: the PC’s local NVMe, or the boot server.',
});
/** How an edit ended (spec/25 §10). Null while it is open. */
export const BOOT_EDIT_OUTCOMES = ['published', 'discarded', 'failed'];
export const BootEditOutcomeSchema = z.enum(BOOT_EDIT_OUTCOMES).openapi('BootEditOutcome', {
    description: 'How an edit ended: published as a new version, discarded, or it failed (its preparation, or the boot server could not open it).',
});
/**
 * Where an edit is written (spec/25 §10, §12): `pc` — an edit boot, one PC booting the image with its writes going into
 * the new version; `server` — a server edit, a game disk's new version attached read-write on the boot server itself, on
 * the game disk's drive letter, for staff to install and update games there with the launchers on the server.
 */
export const BOOT_EDIT_TARGETS = ['pc', 'server'];
export const BootEditTargetSchema = z.enum(BOOT_EDIT_TARGETS).openapi('BootEditTarget', {
    description: 'Where an edit is written: on one PC booted into it (an edit boot), or on the boot server itself (a game disk’s server edit).',
});
/**
 * A game disk's drive letter (spec/25 §12): the same on the boot server (a server edit mounts on it) and on every PC (the
 * Clavian Boot client assigns it at boot), so a launcher library on it is one path everywhere. `D`–`Z`: never `A`–`C`.
 */
export const BootDriveLetterSchema = z
    .string()
    .regex(/^[D-Z]$/)
    .openapi('BootDriveLetter', { description: 'A game disk’s drive letter, one upper-case letter from D to Z.', example: 'G' });
/** The letter a game disk gets when none is chosen; the next free one after it when it is taken. */
export const BOOT_DEFAULT_GAME_DISK_DRIVE_LETTER = 'G';
/** What a force-wipe clears (spec/25 §5.4, §11). */
export const BOOT_WIPE_KINDS = ['writeback', 'shaderCache'];
export const BootWipeKindSchema = z.enum(BOOT_WIPE_KINDS).openapi('BootWipeKind', {
    description: 'What a force-wipe clears: a PC’s write-back, or shader-cache volumes.',
});
/** Which PCs a force-wipe is for. `family` is for shader caches only: the PCs of one GPU family. */
export const BOOT_WIPE_SCOPES = ['pc', 'family', 'fleet'];
export const BootWipeScopeSchema = z.enum(BOOT_WIPE_SCOPES).openapi('BootWipeScope', {
    description: 'Which PCs a force-wipe is for: one PC, one GPU family (shader caches only), or every PC at the venue.',
});
/** A GPU family: vendor and driver branch, as the boot PC reports it ("nvidia-r565"). */
export const BootGpuFamilySchema = z
    .string()
    .regex(/^[a-z0-9][a-z0-9._-]{0,63}$/)
    .openapi('BootGpuFamily', { description: 'A GPU family: vendor and driver branch, lower-case ("nvidia-r565").', example: 'nvidia-r565' });
/** The boot server's id for one boot of a PC: a UUID it mints at a new iSCSI login (spec/25 §13.5, spec/10 §14.2). */
export const BootIdSchema = z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/)
    .openapi('BootId', { description: 'The boot server’s id for one boot of a PC: a lower-case UUID minted at a new iSCSI login.' });
/** A processor's name as Windows reports it (spec/25 §13.4): "AMD Ryzen 5 7600 6-Core Processor". */
export const BootCpuNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(128)
    .openapi('BootCpuName', { description: 'The processor’s name as Windows reports it.', example: 'AMD Ryzen 5 7600 6-Core Processor' });
/**
 * A motherboard model, from SMBIOS's baseboard (type 2) manufacturer and product strings (ADR-0065 decision 8): a Windows
 * image works on the model it was made on, so the reference PC's and each boot PC's are recorded and compared.
 */
export const BootMotherboardSchema = z
    .object({
    manufacturer: z.string().trim().min(1).max(128),
    product: z.string().trim().min(1).max(128),
})
    .meta({
    id: 'BootMotherboard',
    description: 'A motherboard model: SMBIOS baseboard manufacturer and product ("ASUSTeK COMPUTER INC." · "PRIME B650M-A").',
});
/** A PCI vendor and device id, "8086:125C". */
export const BootPciIdSchema = z
    .string()
    .regex(/^[0-9A-F]{4}:[0-9A-F]{4}$/)
    .openapi('BootPciId', { description: 'A PCI vendor and device id, upper-case hex ("8086:125C").', example: '8086:125C' });
/** One data drive on the boot server and what it holds (spec/25 §5.1). */
export const BOOT_DISK_ROLES = ['system', 'images', 'games', 'writeback', 'shader'];
export const BootDiskSchema = z
    .object({
    role: z.enum(BOOT_DISK_ROLES).meta({ id: 'BootDiskRole' }),
    /** The drive or folder, as the server sees it ("D:\\ClavianBoot\\images"). */
    path: z.string().min(1).max(260),
    totalBytes: z.int().min(0),
    freeBytes: z.int().min(0),
})
    .meta({ id: 'BootDisk', description: 'One of the boot server’s data drives: what it holds, its size and its free space.' });
const ENTITLEMENT_REQUIRED = problemResponse('The organisation does not have the Clavian Boot add-on.', 'entitlement-required');
// ---------------------------------------------------------------------------------------------
// Boot server updates (F-BOOT-23, spec/25 §13.6): manual, never automatic
// ---------------------------------------------------------------------------------------------
/** A Clavian Boot server (MSI) version, `major.minor.build[.revision]` — the station's scheme (`1.10.41234.0`). */
export const BootServerVersionSchema = z
    .string()
    .regex(/^\d{1,5}(\.\d{1,9}){2,3}$/)
    .openapi('BootServerVersion', { description: 'A Clavian Boot server (MSI) version, `major.minor.build[.revision]`.', example: '1.10.41234.0' });
/**
 * Where a boot server's own update is (spec/25 §13.6). Nothing installs by itself: a verified update waits in
 * `available` until staff press Update now.
 *
 *   none         nothing newer is offered (or the server has not checked yet)
 *   downloading  a newer release's signed manifest verified; its MSI is downloading
 *   available    the MSI is on the server, its sha256 and size checked: waiting for Update now
 *   installing   staff pressed Update now: the installer is running (the service restarts)
 *   installed    the last update went in and the new version came back
 *   failed       a download, a check or an install failed (`detail` says which); the old version still runs
 *   rolled_back  the new version did not come back in time; the previous one was reinstalled
 */
export const BOOT_SERVER_UPDATE_STATES = ['none', 'downloading', 'available', 'installing', 'installed', 'failed', 'rolled_back'];
export const BootServerUpdateStateSchema = z.enum(BOOT_SERVER_UPDATE_STATES).openapi('BootServerUpdateState', {
    description: 'Where the boot server’s own update is: none, downloading, available, installing, installed, failed or rolled_back.',
});
/** How a boot server's last install ended. */
export const BOOT_SERVER_UPDATE_OUTCOMES = ['installed', 'failed', 'rolled_back'];
export const BootServerUpdateResultSchema = z
    .object({
    /** The version that ran before. */
    fromVersion: BootServerVersionSchema,
    /** The version it tried to install. */
    toVersion: BootServerVersionSchema,
    outcome: z.enum(BOOT_SERVER_UPDATE_OUTCOMES).meta({ id: 'BootServerUpdateOutcome' }),
    /** Why it failed or was rolled back; null when it went in. */
    detail: z.string().max(500).nullable(),
    at: IsoDateTimeSchema,
})
    // `.meta({ id })` rather than `.openapi()`: the realtime status payload carries it too, and its JSON schema (and the
    // boot server's C# record) needs the name (as `BootDisk`).
    .meta({ id: 'BootServerUpdateResult', description: 'How the boot server’s last update ended: installed, failed or rolled back.' });
export const BootServerUpdateSchema = z
    .object({
    /** The version installed now, as the server reported it. */
    installedVersion: BootServerVersionSchema.nullable(),
    /** A newer release downloaded and verified on the server, waiting for Update now; null when none. */
    availableVersion: BootServerVersionSchema.nullable(),
    state: BootServerUpdateStateSchema,
    /** One line about the state: what failed, why an Update now was refused. */
    detail: z.string().max(500).nullable(),
    /** PCs booted from the server (a live iSCSI session) when it last reported: an update restarts the service under them. */
    bootedPcs: z.int().min(0).nullable(),
    lastUpdate: BootServerUpdateResultSchema.nullable(),
})
    .openapi('BootServerUpdate', {
    description: 'The boot server’s own software: what is installed, what is waiting to be installed, and how the last update went.',
});
// ---------------------------------------------------------------------------------------------
// Servers
// ---------------------------------------------------------------------------------------------
/** A network start the boot server saw (step D of ADR-0065): when, and the PC's MAC. */
export const BootNetworkStartSeenSchema = z
    .object({ at: IsoDateTimeSchema, mac: BootMacSchema })
    .openapi('BootNetworkStartSeen', { description: 'The last network start (a PC’s PXE request) the boot server saw.' });
export const BootServerSchema = z
    .object({
    id: BootServerIdSchema,
    venueId: VenueIdSchema,
    name: z.string().min(1).max(80),
    status: BootServerStatusSchema,
    /** The Clavian Boot service version it last reported. Null until it has. */
    version: z.string().max(64).nullable(),
    pairedAt: IsoDateTimeSchema.nullable(),
    lastSeenAt: IsoDateTimeSchema.nullable(),
    disks: z.array(BootDiskSchema),
    /** The lease state it last reported. Null until it has. */
    licenceState: BootLicenceStateSchema.nullable(),
    /** The RAM cache it runs with (spec/25 §7), in bytes. Null until reported. */
    ramCacheBytes: z.int().min(0).nullable(),
    /** Its own software update (spec/25 §13.6). Null until a server that updates has reported. */
    update: BootServerUpdateSchema.nullable(),
    // Live boot status (step D of ADR-0065, spec/25 §13.5). Optional, additions: absent from an api built before.
    /**
     * When it last sent its live summary of every PC it knows (`boot.pcs.summary`, about every 30 s). Null from a server
     * that does not send one (built before): its PCs' states are then derived from their last start only.
     */
    liveReportedAt: IsoDateTimeSchema.nullable().optional(),
    /**
     * The last network start (a PC's PXE request) it saw, any PC's, known to the venue or not — what the guided setup's
     * test waits for. Null before it reported one.
     */
    lastNetworkStart: BootNetworkStartSeenSchema.nullable().optional(),
})
    .openapi('BootServer', { description: 'A venue’s Clavian Boot server: its status, version, disks and licence state.' });
export const BootServerListSchema = z.object({ servers: z.array(BootServerSchema) }).openapi('BootServerList');
export const CreateBootServerPairingCodeSchema = z
    .object({
    /** What staff call it. The venue's existing server keeps its name when absent. */
    name: z.string().trim().min(1).max(80).optional(),
})
    .openapi('CreateBootServerPairingCode');
export const BootServerPairingCodeSchema = z
    .object({
    bootServerId: BootServerIdSchema,
    /** Eight characters, shown once: typed into the boot server's first-run screen. Only its hash is stored. */
    code: z.string().length(8),
    expiresAt: IsoDateTimeSchema,
})
    .openapi('BootServerPairingCode', { description: 'A one-time code that pairs a boot server to this venue, valid 30 minutes.' });
export const UpdateBootServerSchema = z.object({ name: z.string().trim().min(1).max(80) }).openapi('UpdateBootServer');
const BootServerParamsSchema = VenueScopeParamsSchema.extend({ bootServerId: BootServerIdSchema });
const BOOT_BASE = '/v1/organizations/{organizationId}/venues/{venueId}/boot';
export const listBootServersRoute = createRoute({
    method: 'get',
    path: `${BOOT_BASE}/servers`,
    operationId: 'listBootServers',
    tags: ['boot'],
    summary: 'The venue’s boot server (F-BOOT-01)',
    description: 'Its status, version, disks, free space, licence state and RAM cache. Requires `boot:view` and the `boot` add-on.',
    [PERMISSION_EXTENSION]: BOOT_VIEW,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(BootServerListSchema, 'The venue’s boot servers (one at most).'),
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        500: commonProblemResponses[500],
    },
});
export const createBootServerPairingCodeRoute = createRoute({
    method: 'post',
    path: `${BOOT_BASE}/servers/pairing-code`,
    operationId: 'createBootServerPairingCode',
    tags: ['boot'],
    summary: 'A pairing code for the venue’s boot server (F-BOOT-01)',
    description: 'Makes the venue’s boot server (a venue has one) if it has none, and a one-time 8-character code, valid 30 minutes, ' +
        'to type into the server’s first-run screen. A new code replaces an unused one. Pairing an already-paired server ' +
        'again revokes its old device key when the new one is bound. Requires `boot:manage`. Audited.',
    [PERMISSION_EXTENSION]: BOOT_MANAGE,
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: CreateBootServerPairingCodeSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(BootServerPairingCodeSchema, 'The code, shown once.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        500: commonProblemResponses[500],
    },
});
export const updateBootServerRoute = createRoute({
    method: 'patch',
    path: `${BOOT_BASE}/servers/{bootServerId}`,
    operationId: 'updateBootServer',
    tags: ['boot'],
    summary: 'Rename a boot server',
    description: 'Requires `boot:manage`. Audited.',
    [PERMISSION_EXTENSION]: BOOT_MANAGE,
    request: { params: BootServerParamsSchema, body: { content: { [JSON_CONTENT_TYPE]: { schema: UpdateBootServerSchema } }, required: true } },
    responses: {
        200: jsonResponse(BootServerSchema, 'The boot server.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such boot server at this venue.', 'not-found'),
        500: commonProblemResponses[500],
    },
});
export const unpairBootServerRoute = createRoute({
    method: 'post',
    path: `${BOOT_BASE}/servers/{bootServerId}/unpair`,
    operationId: 'unpairBootServer',
    tags: ['boot'],
    summary: 'Unpair a boot server',
    description: 'Revokes the server’s device key at once — its next token is refused and its realtime socket is closed — and sets it ' +
        'back to `pairing`. It keeps booting the PCs it knows from its last config and lease (spec/25 §13.3) until it is ' +
        'paired again. Requires `boot:manage`. Audited.',
    [PERMISSION_EXTENSION]: BOOT_MANAGE,
    request: { params: BootServerParamsSchema },
    responses: {
        200: jsonResponse(BootServerSchema, 'The boot server, now waiting to be paired.'),
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such boot server at this venue.', 'not-found'),
        500: commonProblemResponses[500],
    },
});
export const UpdateBootServerSoftwareSchema = z
    .object({
    /**
     * Install even though PCs are booted from the server. Off: the server refuses while any PC has a live session and
     * says how many. On: the service restarts under them (Windows' iSCSI initiator usually rides out a short restart,
     * and the PCs' write-backs are kept on disk, but a PC can still hang if the restart takes too long).
     */
    force: z.boolean(),
})
    .openapi('UpdateBootServerSoftware', { description: 'Install the update the boot server has downloaded and verified.' });
export const BootServerUpdateRequestSchema = z
    .object({
    bootServerId: BootServerIdSchema,
    /** The version the server was told to install: the one it reported downloaded and verified. */
    version: BootServerVersionSchema,
    force: z.boolean(),
    requestedAt: IsoDateTimeSchema,
})
    .openapi('BootServerUpdateRequest', {
    description: 'Update now was sent to the boot server. Its progress and result come back in the server’s status.',
});
export const updateBootServerSoftwareRoute = createRoute({
    method: 'post',
    path: `${BOOT_BASE}/servers/{bootServerId}/update`,
    operationId: 'updateBootServerSoftware',
    tags: ['boot'],
    summary: 'Install the boot server’s downloaded update now (F-BOOT-23)',
    description: 'Boot servers never install an update by themselves (spec/25 §13.6): each checks for a newer release, downloads ' +
        'it and verifies its signature, sha256 and size, then waits. This tells the server to install the version it ' +
        'reported available — `boot.server.update` over its socket, sent only while it is online. Without `force` the ' +
        'server refuses while any PC is booted from it and reports how many; with it, the service restarts under them. ' +
        'If the new version does not come back, the server reinstalls the previous one. The result arrives in ' +
        '`boot.server.status`. `409` when the server is offline or has no update waiting. Requires `boot:manage`. Audited.',
    [PERMISSION_EXTENSION]: BOOT_MANAGE,
    request: {
        params: BootServerParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: UpdateBootServerSoftwareSchema } }, required: true },
    },
    responses: {
        202: jsonResponse(BootServerUpdateRequestSchema, 'Update now was sent to the boot server.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such boot server at this venue.', 'not-found'),
        409: problemResponse('The boot server is offline, has no update waiting, or is installing one already.', 'conflict'),
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Images and versions
// ---------------------------------------------------------------------------------------------
export const BootEditSchema = z
    .object({
    id: BootEditIdSchema,
    imageId: BootImageIdSchema,
    /** The unpublished version the edit writes into. */
    versionId: BootImageVersionIdSchema,
    /** `pc`: an edit boot on `bootPcId`; `server`: a game disk opened for updates on the boot server itself. */
    target: BootEditTargetSchema,
    /** The PC an edit boot is on; null for a server edit, which holds no PC. */
    bootPcId: BootPcIdSchema.nullable(),
    /** A server edit: when the boot server reported the new version mounted on the game disk's letter; null until then. */
    mountedAt: IsoDateTimeSchema.nullable(),
    /** A server edit that failed: why, in the boot server's words (the letter is in use, the layer could not be made…). */
    detail: z.string().max(500).nullable(),
    startedBy: MemberIdSchema.nullable(),
    startedAt: IsoDateTimeSchema,
    endedAt: IsoDateTimeSchema.nullable(),
    outcome: BootEditOutcomeSchema.nullable(),
})
    .openapi('BootEdit', {
    description: 'An edit: a new version of an image written by one PC booted into it, or — a game disk only — on the boot server itself.',
});
/** The jobs that make a version, whose latest progress the version keeps while it runs. */
export const BOOT_VERSION_JOBS = ['capture', 'import', 'preparation'];
export const BootVersionJobSchema = z.enum(BOOT_VERSION_JOBS).openapi('BootVersionJob', {
    description: 'The job making a version: a capture from a reference PC, an import of a file on the boot server, or a preparation.',
});
export const BootVersionProgressSchema = z
    .object({
    job: BootVersionJobSchema,
    /** 0–100, when the job can tell. */
    percent: z.int().min(0).max(100).nullable(),
    /** One line for staff: the step it is on, in the boot server's words. */
    message: z.string().max(500).nullable(),
    /** A capture's bytes received so far, and the bytes it expects in all; null when the job does not count bytes. */
    bytesDone: z.int().min(0).nullable(),
    bytesTotal: z.int().min(0).nullable(),
    /** When the boot server reported it. */
    updatedAt: IsoDateTimeSchema,
})
    .openapi('BootVersionProgress', {
    description: 'How far the job making a version has got, as the boot server last reported it. Saved every few seconds while it runs, so it survives a refresh.',
});
/**
 * Where a new image's first version comes from (spec/25 §9.1, §9.6, §12): captured from a reference PC; imported from a
 * VHD, VHDX or VMDK on the boot server; or — a game disk only — made from a drive or folder on the boot server, whose files
 * are copied into a new NTFS disk.
 */
export const BOOT_IMAGE_SOURCES = ['capture', 'import', 'folder'];
export const BootImageSourceSchema = z.enum(BOOT_IMAGE_SOURCES).openapi('BootImageSource', {
    description: 'Where a new image comes from: captured from a reference PC, imported from a VHD, VHDX or VMDK on the boot server, or (a game disk) made from a folder on the boot server.',
});
/** How a game disk made from a folder treats the folder (spec/25 §12): kept as it is, or emptied as each file moves in. */
export const BOOT_FOLDER_MODES = ['copy', 'move'];
export const BootFolderModeSchema = z.enum(BOOT_FOLDER_MODES).openapi('BootFolderMode', {
    description: 'A game disk from a folder: `copy` keeps the folder; `move` deletes each file from it once its copy on the game disk is checked.',
});
export const BootImageVersionSchema = z
    .object({
    id: BootImageVersionIdSchema,
    imageId: BootImageIdSchema,
    /** 1, 2, 3 … per image. */
    number: z.int().min(1),
    parentVersionId: BootImageVersionIdSchema.nullable(),
    state: BootImageVersionStateSchema,
    sizeBytes: z.int().min(0).nullable(),
    note: z.string().max(500).nullable(),
    createdBy: MemberIdSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    publishedAt: IsoDateTimeSchema.nullable(),
    /** What preparation changed, step by step (spec/25 §9.2), as the boot server reported it. */
    preparationReport: z.looseObject({}).nullable(),
    /** The latest progress of the job making it, while it is capturing or preparing; null otherwise. */
    progress: BootVersionProgressSchema.nullable(),
    /**
     * The motherboard of the reference PC a captured version was made on; null for a version that was not captured, or
     * captured by a client that did not report one. Optional, an addition.
     */
    motherboard: BootMotherboardSchema.nullable().optional(),
    /**
     * How the version's first layer was made: captured, imported or made from a folder. Optional, an addition: the console
     * shows "Move the rest" on a game disk's failed folder version (step D).
     */
    source: BootImageSourceSchema.optional(),
    /** `folder` only: whether the files are copied or moved into the game disk. */
    folderMode: BootFolderModeSchema.nullable().optional(),
})
    .openapi('BootImageVersion', { description: 'One version of an image: a differencing layer, and a restore point once published.' });
export const BootVersionInProgressSchema = z
    .object({
    id: BootImageVersionIdSchema,
    number: z.int().min(1),
    state: BootImageVersionStateSchema,
    progress: BootVersionProgressSchema.nullable(),
})
    .openapi('BootVersionInProgress', { description: 'The version of an image being captured or prepared now, and how far it has got.' });
/**
 * The image another image was saved from (spec/25 §10, "Save as a new image"): its first live version is a differencing
 * layer over this version of that image, so that version cannot be merged away, and that image cannot be archived, until
 * this one is made independent.
 */
export const BootImageBaseSchema = z
    .object({
    imageId: BootImageIdSchema,
    name: z.string(),
    versionId: BootImageVersionIdSchema,
    versionNumber: z.int().min(1),
})
    .openapi('BootImageBase', { description: 'The image and version an image was saved from, which it still stands on.' });
/** An image saved from one of this image's versions, which stands on it until it is made independent. */
export const BootImageDependentSchema = z
    .object({
    imageId: BootImageIdSchema,
    name: z.string(),
    /** The version of *this* image it stands on. */
    onVersionNumber: z.int().min(1),
})
    .openapi('BootImageDependent', { description: 'An image built on one of this image’s versions.' });
export const BootImageSchema = z
    .object({
    id: BootImageIdSchema,
    venueId: VenueIdSchema,
    name: z.string().min(1).max(80),
    kind: BootImageKindSchema,
    currentVersionId: BootImageVersionIdSchema.nullable(),
    currentVersionNumber: z.int().min(1).nullable(),
    /** The system image an unassigned PC boots. One per venue. Always false for a game disk. */
    defaultForVenue: z.boolean(),
    nicDriverMode: BootNicDriverModeSchema,
    /** The families ticked in `families` mode. */
    nicFamilies: z.array(z.string().min(1).max(64)),
    /** A game disk's system images, or null for every one (spec/25 §12). Always null for a system image. */
    appliesToImageIds: z.array(BootImageIdSchema).nullable(),
    /** A game disk's drive letter on the boot server and on every PC (spec/25 §12). Always null for a system image. */
    driveLetter: BootDriveLetterSchema.nullable(),
    /** A merge the boot server has been asked to run, up to and including this version; null when none is pending. */
    pendingMergeUpToVersionId: BootImageVersionIdSchema.nullable(),
    /** The edit open on this image, if any: an edit boot, or a game disk's server edit. */
    openEdit: BootEditSchema.nullable(),
    /** The newest version being captured or prepared, with its latest progress; null when none is. */
    versionInProgress: BootVersionInProgressSchema.nullable(),
    archivedAt: IsoDateTimeSchema.nullable(),
    // Step C of ADR-0065 (F-BOOT-27). Optional, additions.
    /** The image this one was saved from and still stands on; null for an independent image. */
    basedOn: BootImageBaseSchema.nullable().optional(),
    /** The images saved from this one's versions that still stand on them. */
    dependents: z.array(BootImageDependentSchema).optional(),
    /** The station classes whose PCs boot it (a system image) or see it (a game disk). */
    classIds: z.array(StationClassIdSchema).optional(),
    /** The motherboard its current version was made on (the capture at the root of its chain); null when not known. */
    motherboard: BootMotherboardSchema.nullable().optional(),
})
    .openapi('BootImage', { description: 'A boot image or game disk, with its current version and any open edit.' });
export const BootImageListSchema = z.object({ images: z.array(BootImageSchema) }).openapi('BootImageList');
export const BootImageVersionListSchema = z.object({ versions: z.array(BootImageVersionSchema) }).openapi('BootImageVersionList');
export const CreateBootImageSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    kind: BootImageKindSchema,
    source: BootImageSourceSchema,
    /**
     * `import`: the file on the boot server to import (a CCBoot or ggRock VHD, VHDX or VMDK). `folder`: the drive or folder
     * on the boot server whose files make the game disk (`D:\\Games`, or the root of an attached CCBoot game disk).
     */
    importPath: z.string().min(1).max(260).optional(),
    /**
     * `folder` only: `copy` keeps the folder as it is; `move` moves each file into the game disk — copied in, checked by
     * size and SHA-256, then deleted from the folder — so a large library is never held twice. Absent is `copy`.
     */
    folderMode: BootFolderModeSchema.optional(),
    /** A game disk's drive letter; the venue's first free letter from `G` when absent. Game disks only. */
    driveLetter: BootDriveLetterSchema.optional(),
    /** Make it the image unassigned PCs boot. System images only. */
    defaultForVenue: z.boolean().optional(),
    nicDriverMode: BootNicDriverModeSchema.optional(),
    nicFamilies: z.array(z.string().min(1).max(64)).max(32).optional(),
})
    .openapi('CreateBootImage');
export const CreatedBootImageSchema = z
    .object({
    image: BootImageSchema,
    /** Version 1, `capturing` (a capture) or `preparing` (an import, or a game disk made from a folder). */
    version: BootImageVersionSchema,
    /** `capture`: the one-time capture code for the reference PC, shown once. Null for an import. */
    captureCode: z.string().nullable(),
    captureCodeExpiresAt: IsoDateTimeSchema.nullable(),
})
    .openapi('CreatedBootImage', { description: 'A new image and its first version, with the capture code when it is captured.' });
export const UpdateBootImageSchema = z
    .object({
    name: z.string().trim().min(1).max(80).optional(),
    defaultForVenue: z.boolean().optional(),
    nicDriverMode: BootNicDriverModeSchema.optional(),
    nicFamilies: z.array(z.string().min(1).max(64)).max(32).optional(),
    /** A game disk's system images, or null for every one. */
    appliesToImageIds: z.array(BootImageIdSchema).max(64).nullable().optional(),
    /**
     * A game disk's drive letter: one per game disk of the venue. PCs take it at their next boot; refused while the game
     * disk is open for updates on the server (it is mounted on the old letter).
     */
    driveLetter: BootDriveLetterSchema.optional(),
    /** Archive (true) or restore (false). An archived image boots nothing. */
    archived: z.boolean().optional(),
})
    .openapi('UpdateBootImage');
export const RestoreBootImageSchema = z.object({ versionId: BootImageVersionIdSchema }).openapi('RestoreBootImage');
export const MergeBootImageSchema = z.object({ upToVersionId: BootImageVersionIdSchema }).openapi('MergeBootImage');
export const PrepareBootImageVersionSchema = z.object({ versionId: BootImageVersionIdSchema }).openapi('PrepareBootImageVersion');
const BootImageParamsSchema = VenueScopeParamsSchema.extend({ imageId: BootImageIdSchema });
const imageNotFound = problemResponse('No such image at this venue.', 'not-found');
export const listBootImagesRoute = createRoute({
    method: 'get',
    path: `${BOOT_BASE}/images`,
    operationId: 'listBootImages',
    tags: ['boot'],
    summary: 'The venue’s images and game disks (F-BOOT-05, F-BOOT-13)',
    description: 'Each with its current version and open edit. Requires `boot:view` and the `boot` add-on.',
    [PERMISSION_EXTENSION]: BOOT_VIEW,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(BootImageListSchema, 'The images.'),
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        500: commonProblemResponses[500],
    },
});
export const createBootImageRoute = createRoute({
    method: 'post',
    path: `${BOOT_BASE}/images`,
    operationId: 'createBootImage',
    tags: ['boot'],
    summary: 'A new image: capture it from a reference PC, import one, or make a game disk from a folder (F-BOOT-05, F-BOOT-08, F-BOOT-13, F-BOOT-18)',
    description: 'Makes the image and its version 1. A capture answers a one-time capture code (valid 2 hours) for the reference PC; ' +
        'an import asks the boot server to import the file it names; a game disk `folder` asks it to copy the files of a ' +
        'drive or folder on the server into a new NTFS disk and publish it. Each way the boot server is told ' +
        '(`boot.capture.start`). A game disk gets a drive letter, the first free one from `G` unless one is given. ' +
        'Requires `boot:manage`. Audited.',
    [PERMISSION_EXTENSION]: BOOT_MANAGE,
    request: { params: VenueScopeParamsSchema, body: { content: { [JSON_CONTENT_TYPE]: { schema: CreateBootImageSchema } }, required: true } },
    responses: {
        201: jsonResponse(CreatedBootImageSchema, 'The new image.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        409: problemResponse('The venue has no paired boot server to capture or import to, or that drive letter is another game disk’s.', 'conflict'),
        500: commonProblemResponses[500],
    },
});
export const updateBootImageRoute = createRoute({
    method: 'patch',
    path: `${BOOT_BASE}/images/{imageId}`,
    operationId: 'updateBootImage',
    tags: ['boot'],
    summary: 'Change an image',
    description: 'Its name, whether it is the venue default, its NIC drivers, a game disk’s images or drive letter, or archive it. ' +
        'Requires `boot:manage`. Audited.',
    [PERMISSION_EXTENSION]: BOOT_MANAGE,
    request: { params: BootImageParamsSchema, body: { content: { [JSON_CONTENT_TYPE]: { schema: UpdateBootImageSchema } }, required: true } },
    responses: {
        200: jsonResponse(BootImageSchema, 'The image.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: imageNotFound,
        409: problemResponse('That drive letter is another game disk’s, or the game disk is open for updates on the server.', 'conflict'),
        500: commonProblemResponses[500],
    },
});
export const listBootImageVersionsRoute = createRoute({
    method: 'get',
    path: `${BOOT_BASE}/images/{imageId}/versions`,
    operationId: 'listBootImageVersions',
    tags: ['boot'],
    summary: 'An image’s versions: its restore points (F-BOOT-05)',
    description: 'Newest first. Requires `boot:view` and the `boot` add-on.',
    [PERMISSION_EXTENSION]: BOOT_VIEW,
    request: { params: BootImageParamsSchema },
    responses: {
        200: jsonResponse(BootImageVersionListSchema, 'The versions.'),
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: imageNotFound,
        500: commonProblemResponses[500],
    },
});
export const restoreBootImageRoute = createRoute({
    method: 'post',
    path: `${BOOT_BASE}/images/{imageId}/restore`,
    operationId: 'restoreBootImage',
    tags: ['boot'],
    summary: 'Make an earlier version current again (F-BOOT-05)',
    description: 'A new assignment, not a rewrite: PCs boot the restored version at their next boot. Only a published version can be ' +
        'restored. Requires `boot:manage`. Audited.',
    [PERMISSION_EXTENSION]: BOOT_MANAGE,
    request: { params: BootImageParamsSchema, body: { content: { [JSON_CONTENT_TYPE]: { schema: RestoreBootImageSchema } }, required: true } },
    responses: {
        200: jsonResponse(BootImageSchema, 'The image, with its restored current version.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: imageNotFound,
        409: problemResponse('That version is not a published version of this image.', 'conflict'),
        500: commonProblemResponses[500],
    },
});
export const mergeBootImageRoute = createRoute({
    method: 'post',
    path: `${BOOT_BASE}/images/{imageId}/merge`,
    operationId: 'mergeBootImage',
    tags: ['boot'],
    summary: 'Merge old versions into a new base (F-BOOT-05)',
    description: 'Asks the boot server to merge every version up to and including `upToVersionId` into a new base, removing those ' +
        'restore points. `upToVersionId` must be published and older than the current version and than every version a PC ' +
        'or an edit still uses. Requires `boot:manage`. Audited.',
    [PERMISSION_EXTENSION]: BOOT_MANAGE,
    request: { params: BootImageParamsSchema, body: { content: { [JSON_CONTENT_TYPE]: { schema: MergeBootImageSchema } }, required: true } },
    responses: {
        202: jsonResponse(BootImageSchema, 'The image, with the merge pending on the boot server.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: imageNotFound,
        409: problemResponse('That version cannot be merged: it is in use, not published, or a merge is already pending.', 'conflict'),
        500: commonProblemResponses[500],
    },
});
export const prepareBootImageVersionRoute = createRoute({
    method: 'post',
    path: `${BOOT_BASE}/images/{imageId}/prepare`,
    operationId: 'prepareBootImageVersion',
    tags: ['boot'],
    summary: 'Prepare a version again (F-BOOT-09)',
    description: 'Runs the boot server’s full preparation again, with no new capture. A captured or imported version whose preparation ' +
        'failed (it has a preparation report) goes back to `preparing` on the layer the boot server already holds. A published ' +
        'captured or imported version is left exactly as it is, because PCs may be booted on it: a new version of the same image ' +
        'is made on top of it (a differencing layer, so nothing is copied), prepared, and published when preparation passes, ' +
        'just as after a capture; the response is that new version, in `preparing`, and the published one stays as a restore ' +
        'point. The venue needs a paired boot server, and the image must not be archived, being edited or already preparing a ' +
        'version. A game disk made from a folder whose job failed (source `folder`) goes back to `preparing` the same way, and ' +
        'the boot server runs the folder job again — for a move, it moves the files still in the folder (the console’s “Move ' +
        'the rest”). Requires `boot:manage`. Audited.',
    [PERMISSION_EXTENSION]: BOOT_MANAGE,
    request: { params: BootImageParamsSchema, body: { content: { [JSON_CONTENT_TYPE]: { schema: PrepareBootImageVersionSchema } }, required: true } },
    responses: {
        202: jsonResponse(BootImageVersionSchema, 'The version now preparing: the same one when it had failed, a new one made from the published version otherwise.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: imageNotFound,
        409: problemResponse('Only a captured or imported version that failed or is published, or a folder game disk whose job failed, can be prepared again; the venue needs a paired boot server, and the image must not be archived, being edited or already preparing a version.', 'conflict'),
        500: commonProblemResponses[500],
    },
});
export const makeBootImageIndependentRoute = createRoute({
    method: 'post',
    path: `${BOOT_BASE}/images/{imageId}/make-independent`,
    operationId: 'makeBootImageIndependent',
    tags: ['boot'],
    summary: 'Make an image saved from another independent (F-BOOT-27)',
    description: 'An image saved from another (“Save as a new image”) stands on a version of that image. This asks the boot server to ' +
        'flatten the image’s first version, with every layer under it, into a base of its own — a merge job up to that ' +
        'version, which keeps its file name and identity so the versions above it open unchanged — after which the other ' +
        'image’s version can be merged away and that image archived. Every PC on the image must be shut down for the job to ' +
        'run. `409` for an image that stands on no other, or with a merge pending or an edit open. Requires `boot:manage`. Audited.',
    [PERMISSION_EXTENSION]: BOOT_MANAGE,
    request: { params: BootImageParamsSchema },
    responses: {
        202: jsonResponse(BootImageSchema, 'The image, with the flattening pending on the boot server (`pendingMergeUpToVersionId`).'),
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: imageNotFound,
        409: problemResponse('The image stands on no other image, or a merge is pending or an edit is open.', 'conflict'),
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Edit boots (F-BOOT-11)
// ---------------------------------------------------------------------------------------------
export const StartBootEditSchema = z.object({ bootPcId: BootPcIdSchema }).openapi('StartBootEdit');
export const PublishBootEditSchema = z
    .object({
    note: z.string().trim().max(500),
    /**
     * Save the change as a new system image of its own instead of a new version of this one (an edit boot of a system image
     * only; spec/25 §10, F-BOOT-27): its version 1 is the edit's layer, a differencing layer over the version the edit was
     * made on, and the image it came from is unchanged. Absent: a new version of this image, as before.
     */
    asNewImage: z
        .object({ name: z.string().trim().min(1).max(80) })
        .openapi('PublishBootEditAsNewImage', { description: 'The new system image a change is saved as: its name.' })
        .optional(),
})
    .openapi('PublishBootEdit');
const BootEditParamsSchema = VenueScopeParamsSchema.extend({ editId: BootEditIdSchema });
export const startBootEditRoute = createRoute({
    method: 'post',
    path: `${BOOT_BASE}/images/{imageId}/edits`,
    operationId: 'startBootEdit',
    tags: ['boot'],
    summary: 'Edit an image on one PC (F-BOOT-11)',
    description: 'The boot server makes a new, unpublished version on top of the current one, and the PC’s next boot writes into it. ' +
        'Refused while a session runs on the PC’s station; the station is put into maintenance for the edit’s duration. One ' +
        'edit per image at a time, of either kind. Requires `boot:edit_image`. Audited.',
    [PERMISSION_EXTENSION]: BOOT_EDIT_IMAGE,
    request: { params: BootImageParamsSchema, body: { content: { [JSON_CONTENT_TYPE]: { schema: StartBootEditSchema } }, required: true } },
    responses: {
        201: jsonResponse(BootEditSchema, 'The edit.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such image or PC at this venue.', 'not-found'),
        409: problemResponse('The image is already being edited, has no published version, is a PC in a session, or the venue has no paired boot server.', 'boot-edit-in-progress', 'conflict'),
        500: commonProblemResponses[500],
    },
});
export const startBootServerEditRoute = createRoute({
    method: 'post',
    path: `${BOOT_BASE}/images/{imageId}/server-edits`,
    operationId: 'startBootServerEdit',
    tags: ['boot'],
    summary: 'Update a game disk on the boot server (F-BOOT-13)',
    description: 'A server edit: the boot server makes a new, unpublished version on top of the current one and attaches it read-write ' +
        'on the server itself, on the game disk’s drive letter, so staff install and update games there with the launchers on ' +
        'the server. PCs keep the current version until the edit is published (`edits/{id}/publish`), then take the new one at ' +
        'their next boot. It holds no PC and no licence seat. The server reports when it is mounted, or why it could not open ' +
        'it (the letter is in use on the server…), which ends the edit as `failed`. Game disks only; one edit per game disk at ' +
        'a time, of either kind. Requires `boot:edit_image`. Audited.',
    [PERMISSION_EXTENSION]: BOOT_EDIT_IMAGE,
    request: { params: BootImageParamsSchema },
    responses: {
        201: jsonResponse(BootEditSchema, 'The server edit, waiting for the boot server to mount it.'),
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: imageNotFound,
        409: problemResponse('The game disk is already being edited, is archived, has no published version, is not a game disk, or the venue has no paired boot server.', 'boot-edit-in-progress', 'conflict'),
        500: commonProblemResponses[500],
    },
});
export const publishBootEditRoute = createRoute({
    method: 'post',
    path: `${BOOT_BASE}/edits/{editId}/publish`,
    operationId: 'publishBootEdit',
    tags: ['boot'],
    summary: 'Publish an edit as a new version (F-BOOT-11)',
    description: 'The boot server closes the edit layer and prepares it; once preparation passes it becomes the image’s current version ' +
        'and a restore point, and PCs boot it at their next boot. The station leaves maintenance. A server edit is detached ' +
        'from the boot server first, and needs no preparation (a game disk is data). With `asNewImage` (an edit boot of a ' +
        'system image) the change becomes version 1 of a new system image with that name instead, standing on the version the ' +
        'edit was made on; the image it came from is unchanged. Requires `boot:edit_image`. Audited.',
    [PERMISSION_EXTENSION]: BOOT_EDIT_IMAGE,
    request: { params: BootEditParamsSchema, body: { content: { [JSON_CONTENT_TYPE]: { schema: PublishBootEditSchema } }, required: true } },
    responses: {
        200: jsonResponse(BootEditSchema, 'The ended edit.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such edit at this venue.', 'not-found'),
        409: problemResponse('The edit has already ended, or a new image was asked of a game disk’s or a server edit.', 'conflict'),
        500: commonProblemResponses[500],
    },
});
export const discardBootEditRoute = createRoute({
    method: 'post',
    path: `${BOOT_BASE}/edits/{editId}/discard`,
    operationId: 'discardBootEdit',
    tags: ['boot'],
    summary: 'Discard an edit (F-BOOT-11)',
    description: 'The edit layer is deleted (a server edit is detached from the boot server first) and the station leaves maintenance. ' +
        'Requires `boot:edit_image`. Audited.',
    [PERMISSION_EXTENSION]: BOOT_EDIT_IMAGE,
    request: { params: BootEditParamsSchema },
    responses: {
        200: jsonResponse(BootEditSchema, 'The ended edit.'),
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such edit at this venue.', 'not-found'),
        409: problemResponse('The edit has already ended.', 'conflict'),
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Boot PCs
// ---------------------------------------------------------------------------------------------
/** Whether a PC's game disk volume took its drive letter this boot (spec/25 §12): `assigned` false when another drive holds it. */
export const BootPcGameDiskLetterSchema = z
    .object({ driveLetter: BootDriveLetterSchema, assigned: z.boolean() })
    .openapi('BootPcGameDiskLetter', { description: 'Whether the PC’s game disk took its drive letter at its last start.' });
export const BootPcLastBootSchema = z
    .object({
    at: IsoDateTimeSchema,
    imageId: BootImageIdSchema.nullable(),
    versionId: BootImageVersionIdSchema.nullable(),
    versionNumber: z.int().min(1).nullable(),
    editBoot: z.boolean(),
    bootSeconds: z.int().min(0).nullable(),
    secureBoot: z.boolean().nullable(),
    writebackLocation: BootWritebackLocationSchema.nullable(),
    gpuFamily: BootGpuFamilySchema.nullable(),
    nicPciIds: z.array(BootPciIdSchema),
    /**
     * The boot server's id for that boot (a UUID minted at a new iSCSI login), as `boot.pc.booted` reported it: what a
     * boot attestation is checked against (spec/10 §14.2). Absent from a boot reported by a server built before it.
     */
    bootId: BootIdSchema.optional(),
    /** The processor's name as Windows reports it ("AMD Ryzen 5 7600 6-Core Processor"); absent before one was reported. */
    cpu: BootCpuNameSchema.nullable().optional(),
    /** Its motherboard (SMBIOS baseboard), as the boot server or the PC's client reported it; absent before one was reported. */
    motherboard: BootMotherboardSchema.nullable().optional(),
    /**
     * Whether the boot reached Windows: the PC's Clavian Boot client reported its status for it. False for a boot the
     * server reported on its own after five minutes with no word from the PC. Absent from a server built before.
     */
    reachedWindows: z.boolean().optional(),
    /**
     * Whether the game disk's volume got its drive letter on the PC this boot (spec/25 §12), as its client reported; absent
     * when not reported (no game disk, no client, or a server built before).
     */
    gameDisk: BootPcGameDiskLetterSchema.nullable().optional(),
})
    .openapi('BootPcLastBoot', { description: 'What a boot PC’s last boot was: image, version, time, Secure Boot and where its writes went.' });
export const BootPcSchema = z
    .object({
    id: BootPcIdSchema,
    venueId: VenueIdSchema,
    bootServerId: BootServerIdSchema.nullable(),
    mac: BootMacSchema,
    stationId: StationIdSchema.nullable(),
    /** The image assigned to it; null boots the venue default. */
    imageId: BootImageIdSchema.nullable(),
    /** The image it boots now: its own, or the venue default. Null when there is none. */
    effectiveImageId: BootImageIdSchema.nullable(),
    /** That image's current version number. */
    currentVersionNumber: z.int().min(1).nullable(),
    /** Its last boot ran an older version than the image's current one: it gets the new one at its next boot. */
    behind: z.boolean(),
    /** The game disks assigned to it; null takes the ones assigned to its image. */
    gameDiskIds: z.array(BootImageIdSchema).nullable(),
    /** A write-back policy for this PC alone; null inherits the venue's (or the station's) `boot.writebackPolicy`. */
    writebackPolicy: BootWritebackPolicySchema.nullable(),
    lastBoot: BootPcLastBootSchema.nullable(),
    firstSeenAt: IsoDateTimeSchema,
    removedAt: IsoDateTimeSchema.nullable(),
})
    .openapi('BootPc', { description: 'A PC that network-boots from the venue’s boot server, known by its MAC.' });
export const BootPcListSchema = z.object({ pcs: z.array(BootPcSchema) }).openapi('BootPcList');
export const AddBootPcSchema = z
    .object({
    mac: BootMacSchema,
    stationId: StationIdSchema.optional(),
    imageId: BootImageIdSchema.optional(),
})
    .openapi('AddBootPc');
export const UpdateBootPcSchema = z
    .object({
    stationId: StationIdSchema.nullable().optional(),
    imageId: BootImageIdSchema.nullable().optional(),
    gameDiskIds: z.array(BootImageIdSchema).max(8).nullable().optional(),
    writebackPolicy: BootWritebackPolicySchema.nullable().optional(),
})
    .openapi('UpdateBootPc');
const BootPcParamsSchema = VenueScopeParamsSchema.extend({ bootPcId: BootPcIdSchema });
const pcNotFound = problemResponse('No such boot PC at this venue.', 'not-found');
export const listBootPcsRoute = createRoute({
    method: 'get',
    path: `${BOOT_BASE}/pcs`,
    operationId: 'listBootPcs',
    tags: ['boot'],
    summary: 'The venue’s boot PCs (F-BOOT-01, F-BOOT-06)',
    description: 'Every PC with its station, image and version (and whether it is behind), write-back location, Secure Boot, GPU family ' +
        'and last boot. Removed PCs are left out. Requires `boot:view` and the `boot` add-on.',
    [PERMISSION_EXTENSION]: BOOT_VIEW,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(BootPcListSchema, 'The PCs.'),
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        500: commonProblemResponses[500],
    },
});
export const addBootPcRoute = createRoute({
    method: 'post',
    path: `${BOOT_BASE}/pcs`,
    operationId: 'addBootPc',
    tags: ['boot'],
    summary: 'Add a boot PC by its MAC (F-BOOT-16)',
    description: 'Ahead of its first boot, or to bring back a removed one. Counted against the add-on’s licensed PCs: past ' +
        '`maxBootPcs` it is `409 boot-licence-full`. Requires `boot:manage`. Audited.',
    [PERMISSION_EXTENSION]: BOOT_MANAGE,
    request: { params: VenueScopeParamsSchema, body: { content: { [JSON_CONTENT_TYPE]: { schema: AddBootPcSchema } }, required: true } },
    responses: {
        201: jsonResponse(BootPcSchema, 'The PC.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such venue, station or image here.', 'not-found'),
        409: problemResponse('Every licensed boot PC is in use, or a PC with that MAC is already here.', 'boot-licence-full', 'conflict'),
        500: commonProblemResponses[500],
    },
});
export const updateBootPcRoute = createRoute({
    method: 'patch',
    path: `${BOOT_BASE}/pcs/{bootPcId}`,
    operationId: 'updateBootPc',
    tags: ['boot'],
    summary: 'Assign a boot PC (F-BOOT-05, F-BOOT-06, F-BOOT-13)',
    description: 'Its station link, image, game disks and write-back override. It boots them at its next boot. Requires `boot:manage`. Audited.',
    [PERMISSION_EXTENSION]: BOOT_MANAGE,
    request: { params: BootPcParamsSchema, body: { content: { [JSON_CONTENT_TYPE]: { schema: UpdateBootPcSchema } }, required: true } },
    responses: {
        200: jsonResponse(BootPcSchema, 'The PC.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such boot PC, station or image at this venue.', 'not-found'),
        409: problemResponse('That station is already linked to another boot PC.', 'conflict'),
        500: commonProblemResponses[500],
    },
});
export const removeBootPcRoute = createRoute({
    method: 'post',
    path: `${BOOT_BASE}/pcs/{bootPcId}/remove`,
    operationId: 'removeBootPc',
    tags: ['boot'],
    summary: 'Remove a boot PC',
    description: 'It no longer counts against the licensed PCs, and the boot server no longer boots it. Requires `boot:manage`. Audited.',
    [PERMISSION_EXTENSION]: BOOT_MANAGE,
    request: { params: BootPcParamsSchema },
    responses: {
        200: jsonResponse(BootPcSchema, 'The removed PC.'),
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: pcNotFound,
        409: problemResponse('The PC is being used for an edit boot.', 'conflict'),
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Boot state on the Floor (F-BOOT-24, ADR-0065, spec/25 §14)
// ---------------------------------------------------------------------------------------------
/**
 * How a boot PC stands, in the words the Floor shows: starting from the network, loading Windows, ready, an update
 * waiting for its next start, unable to start (with a `problem`), not started from the boot server yet, or `unknown` — the
 * boot server sends live states but has gone quiet, so the cloud will not show a stale one (step D of ADR-0065).
 * `starting` and `loading_windows` come from the boot server's live per-PC report (`boot.pc.state`, spec/25 §13.5).
 */
export const BOOT_FLOOR_STATES = ['starting', 'loading_windows', 'ready', 'update_waiting', 'cant_start', 'never_started', 'unknown'];
export const BootFloorStateSchema = z.enum(BOOT_FLOOR_STATES).openapi('BootFloorState', {
    description: 'How a boot PC stands on the Floor: starting, loading Windows, ready, update waiting, can’t start, never started, or unknown (the boot server went quiet).',
});
/**
 * Why a boot PC cannot start: the venue has no paired boot server, the boot server is offline, its licence lease has
 * expired, there is no Windows image for it to start, the image was made on another motherboard model, or (step D, from
 * the boot server's live report) its start stopped on the boot server for another reason (`start_failed`).
 */
export const BOOT_FLOOR_PROBLEMS = ['no_boot_server', 'boot_server_offline', 'licence_expired', 'no_image', 'other_motherboard', 'start_failed'];
export const BootFloorProblemSchema = z.enum(BOOT_FLOOR_PROBLEMS).openapi('BootFloorProblem', {
    description: 'Why a boot PC cannot start, as a code the console says in plain words.',
});
/**
 * A PC's live boot phase, as the boot server sees it (step D of ADR-0065, spec/25 §13.5), at the moments it already logs:
 *   network_start    it asked for its boot file (a DHCP/PXE request) — a new start
 *   boot_files       the first-stage loader was sent over TFTP
 *   loading_windows  its iSCSI login was accepted: Windows is loading from the boot server
 *   ready            its Clavian Boot client reported from Windows
 *   failed           its start stopped on the boot server (`reason` says why)
 *   not_reaching     the boot server woke it (Wake-on-LAN) and saw no network start from it within two minutes
 *   off              its iSCSI sessions closed and it has not started again
 */
export const BOOT_PC_PHASES = ['network_start', 'boot_files', 'loading_windows', 'ready', 'failed', 'not_reaching', 'off'];
export const BootPcPhaseSchema = z.enum(BOOT_PC_PHASES).openapi('BootPcPhase', {
    description: 'A PC’s live boot phase on the boot server: network start, boot files sent, loading Windows, ready, failed, woken but not reaching it, or off.',
});
/**
 * Why a start failed on the boot server: its licence does not cover the PC, it has no image to start, the PC's firmware is
 * not x64 UEFI, or its disks could not be opened.
 */
export const BOOT_PC_FAILURES = ['licence', 'no_image', 'unsupported_firmware', 'disk_error'];
export const BootPcFailureSchema = z.enum(BOOT_PC_FAILURES).openapi('BootPcFailure', {
    description: 'Why a PC’s start failed on the boot server.',
});
/**
 * Where a boot PC's Windows image (or game disks) comes from (F-BOOT-27, spec/25 §14): set for this PC in its drawer, its
 * station's class, or the venue default (for game disks: those assigned to its image).
 */
export const BOOT_CHOICE_SOURCES = ['pc', 'class', 'venue'];
export const BootChoiceSourceSchema = z.enum(BOOT_CHOICE_SOURCES).openapi('BootChoiceSource', {
    description: 'Where a boot PC’s image or game disks come from: set for this PC, its station’s class, or the venue default (the image’s own games).',
});
export const BootFloorImageSchema = z
    .object({
    id: BootImageIdSchema,
    name: z.string(),
    /** True when the PC has its own image; false when it starts its class's or the venue default. */
    own: z.boolean(),
    /** Where it comes from: this PC's own, its class's, or the venue default. Optional, an addition. */
    from: BootChoiceSourceSchema.optional(),
    /** The version its last start ran; null before one. */
    versionNumber: z.int().min(1).nullable(),
    /** The image's current version; null when it has none published. */
    currentVersionNumber: z.int().min(1).nullable(),
    /** Its last start ran the current version. */
    latest: z.boolean(),
})
    .openapi('BootFloorImage', { description: 'The Windows image a boot PC starts, and whether it runs the latest version.' });
export const BootFloorGameDiskSchema = z
    .object({ id: BootImageIdSchema, name: z.string(), driveLetter: BootDriveLetterSchema.nullable() })
    .openapi('BootFloorGameDisk', { description: 'A game disk a boot PC sees, and its drive letter.' });
export const BootFloorPcSchema = z
    .object({
    bootPcId: BootPcIdSchema,
    stationId: StationIdSchema,
    mac: BootMacSchema,
    state: BootFloorStateSchema,
    /**
     * Why it cannot start: present when `state` is `cant_start`, absent otherwise (optional rather than nullable: the
     * client generators carry no nullable enums).
     */
    problem: BootFloorProblemSchema.optional(),
    /** A change of its image (an edit boot) is open on this PC. */
    editing: z.boolean(),
    image: BootFloorImageSchema.nullable(),
    /** The game disks it sees: its own list, else those assigned to its image (or to every image). */
    gameDisks: z.array(BootFloorGameDiskSchema),
    lastStartAt: IsoDateTimeSchema.nullable(),
    bootSeconds: z.int().min(0).nullable(),
    secureBoot: z.boolean().nullable(),
    gpuFamily: BootGpuFamilySchema.nullable(),
    writebackLocation: BootWritebackLocationSchema.nullable(),
    /** Its own write-back override; null inherits the venue's. */
    writebackPolicy: BootWritebackPolicySchema.nullable(),
    // Step C of ADR-0065 (F-BOOT-27). Optional, additions.
    /** Where its game disks come from: its own list, its class's, or those assigned to its image (`venue`). */
    gameDisksFrom: BootChoiceSourceSchema.optional(),
    /** Its own image override, or null when it takes its class's or the venue default. */
    ownImageId: BootImageIdSchema.nullable().optional(),
    /** Its own game-disk list, or null when it takes its class's or its image's. */
    ownGameDiskIds: z.array(BootImageIdSchema).nullable().optional(),
    /** Its motherboard, from its last start; null when not known. */
    motherboard: BootMotherboardSchema.nullable().optional(),
    /** The motherboard its image was made on; null when not known. Differing from `motherboard`, it may not start. */
    imageMotherboard: BootMotherboardSchema.nullable().optional(),
    // Step D of ADR-0065. Optional, additions.
    /** Since when it has been in a live state (`starting`, `loading_windows`, or a live `cant_start`); null otherwise. */
    stateSince: IsoDateTimeSchema.nullable().optional(),
    /** The game disk's letter it could not take at its last start (another drive of the PC holds it); null otherwise. */
    gameDiskLetterBlocked: BootDriveLetterSchema.nullable().optional(),
    /**
     * Its live phase as the boot server last reported it (`boot.pc.state`), while that report is fresh; null when there is
     * none (off, a server built before, or the boot server gone quiet). `not_reaching`: woken, but no network start seen.
     */
    livePhase: BootPcPhaseSchema.nullable().optional(),
})
    .openapi('BootFloorPc', { description: 'A boot PC linked to a station, summarised for the Floor’s tile and the station window.' });
export const BootFloorSchema = z
    .object({ pcs: z.array(BootFloorPcSchema) })
    .openapi('BootFloor', { description: 'Every live boot PC linked to a station, with its boot state, for the Floor.' });
export const getBootFloorRoute = createRoute({
    method: 'get',
    path: `${BOOT_BASE}/floor`,
    operationId: 'getBootFloor',
    tags: ['boot'],
    summary: 'Boot state of the PCs on the Floor (F-BOOT-24)',
    description: 'One row per live boot PC linked to a station: its boot state (and why it cannot start), image and version, game disks ' +
        'and their letters, last start, Secure Boot and graphics family. PCs not linked to a station are left out. A sibling of ' +
        'the floor snapshot, so boot facts stay behind `boot:view`. Requires `boot:view` and the `boot` add-on.',
    [PERMISSION_EXTENSION]: BOOT_VIEW,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(BootFloorSchema, 'The boot PCs on the Floor.'),
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        500: commonProblemResponses[500],
    },
});
/**
 * The simple boot state of each PC on the Floor, for every member who can see the Floor (F-BOOT-24, spec/13 §3.8): the
 * state and why it cannot start, and nothing else — no image, MAC, version or hardware, which stay behind `boot:view`.
 */
export const BootFloorStateRowSchema = z
    .object({
    stationId: StationIdSchema,
    state: BootFloorStateSchema,
    /** Why it cannot start: present only when `state` is `cant_start`. */
    problem: BootFloorProblemSchema.optional(),
})
    .openapi('BootFloorStateRow', { description: 'One PC’s boot state on the Floor, without any boot detail.' });
export const BootFloorStatesSchema = z
    .object({ pcs: z.array(BootFloorStateRowSchema) })
    .openapi('BootFloorStates', { description: 'The boot state of every PC on the Floor linked to a boot PC.' });
export const getBootFloorStatesRoute = createRoute({
    method: 'get',
    path: `${BOOT_BASE}/floor-states`,
    operationId: 'getBootFloorStates',
    tags: ['boot'],
    summary: 'The simple boot state of the PCs on the Floor (F-BOOT-24)',
    description: 'For a member who sees the Floor without `boot:view` (a cashier): one row per live boot PC linked to a station, with ' +
        'its state and, when it cannot start, the reason code — the same derivation as `GET floor`, and nothing more. Requires ' +
        '`floor:read` and the `boot` add-on (`402` without it: the console then shows no boot state).',
    [PERMISSION_EXTENSION]: FLOOR_READ,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(BootFloorStatesSchema, 'The boot states.'),
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// What a class's PCs boot (F-BOOT-27, ADR-0065 decision 5, spec/13 §12, spec/25 §14)
// ---------------------------------------------------------------------------------------------
/** A station class's Windows image and game disks; null is "not set" (the venue default, the image's games). */
export const BootClassSchema = z
    .object({
    classId: StationClassIdSchema,
    /** The system image its PCs boot; null: the venue default. */
    imageId: BootImageIdSchema.nullable(),
    /** The game disks its PCs see; null: those assigned to the image they boot. */
    gameDiskIds: z.array(BootImageIdSchema).nullable(),
})
    .openapi('BootClass', { description: 'What a station class’s Clavian-booted PCs start: its Windows image and game disks.' });
export const BootClassListSchema = z
    .object({ classes: z.array(BootClassSchema) })
    .openapi('BootClassList', { description: 'Every station class of the venue, with what its PCs boot.' });
export const UpdateBootClassSchema = z
    .object({
    imageId: BootImageIdSchema.nullable(),
    gameDiskIds: z.array(BootImageIdSchema).max(8).nullable(),
})
    .strict()
    .openapi('UpdateBootClass');
export const listBootClassesRoute = createRoute({
    method: 'get',
    path: `${BOOT_BASE}/classes`,
    operationId: 'listBootClasses',
    tags: ['boot'],
    summary: 'What each station class’s PCs boot (F-BOOT-27)',
    description: 'Every station class of the venue with its Windows image and game disks (null: not set). Requires `boot:view` and the `boot` add-on.',
    [PERMISSION_EXTENSION]: BOOT_VIEW,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(BootClassListSchema, 'The classes.'),
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        500: commonProblemResponses[500],
    },
});
export const updateBootClassRoute = createRoute({
    method: 'put',
    path: `${BOOT_BASE}/classes/{classId}`,
    operationId: 'updateBootClass',
    tags: ['boot'],
    summary: 'Set what a station class’s PCs boot (F-BOOT-27)',
    description: 'The class’s Windows image (a live system image of the venue, or null for the venue default) and game disks (live game ' +
        'disks of the venue, or null for the image’s own). A boot PC boots its own image if one is set, else its class’s, else ' +
        'the venue default; its PCs switch at their next start. `400 validation-failed` for an image that is archived, not of ' +
        'the venue or of the wrong kind. Requires `boot:manage` **and** `stations:manage`. Audited (`boot.class.updated`).',
    [PERMISSION_EXTENSION]: BOOT_MANAGE,
    request: {
        params: VenueScopeParamsSchema.extend({ classId: StationClassIdSchema }),
        body: { content: { [JSON_CONTENT_TYPE]: { schema: UpdateBootClassSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(BootClassSchema, 'The class, with what its PCs boot.'),
        400: problemResponse('That image or game disk is archived, not of this venue, or of the wrong kind.', 'validation-failed'),
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such station class at this venue.', 'not-found'),
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// New PCs: add a PC from the Floor (F-BOOT-25, ADR-0065 decision 4, spec/13 §3.9, spec/25 §14)
// ---------------------------------------------------------------------------------------------
/** How long a PC shows its number when staff press Identify (spec/13 §3.9). */
export const BOOT_IDENTIFY_SECONDS = 60;
/** A PC that starts from the boot server and is not a station yet: a card in the Floor's New PCs tray. */
export const BootNewPcSchema = z
    .object({
    bootPcId: BootPcIdSchema,
    mac: BootMacSchema,
    /** Its graphics family from its last start ("nvidia-r565"); null before one. */
    gpuFamily: BootGpuFamilySchema.nullable(),
    /** Its processor, as Windows reports it; null before a client reported one. */
    cpu: BootCpuNameSchema.nullable(),
    /** Its network cards' PCI ids from its last start (the first is the one it started from, when known). */
    nicPciIds: z.array(BootPciIdSchema),
    firstSeenAt: IsoDateTimeSchema,
    /** Its last start; null before one. */
    lastStartAt: IsoDateTimeSchema.nullable(),
    /** Until when it shows its number after an Identify; null when it is not showing one. */
    identifyUntil: IsoDateTimeSchema.nullable(),
    /** Its motherboard, from its last start; null when not known. Optional, an addition. */
    motherboard: BootMotherboardSchema.nullable().optional(),
    /** The image it would start as a station (its own, else the venue default), and the motherboard that image was made on. */
    imageName: z.string().nullable().optional(),
    imageMotherboard: BootMotherboardSchema.nullable().optional(),
})
    .openapi('BootNewPc', { description: 'A PC that starts from the boot server and is not a station yet (the Floor’s New PCs tray).' });
export const BootNewPcListSchema = z
    .object({ pcs: z.array(BootNewPcSchema) })
    .openapi('BootNewPcList', { description: 'The live boot PCs not linked to a station, newest first.' });
export const listBootNewPcsRoute = createRoute({
    method: 'get',
    path: `${BOOT_BASE}/new-pcs`,
    operationId: 'listBootNewPcs',
    tags: ['boot'],
    summary: 'PCs that start from the boot server and are not stations yet (F-BOOT-25)',
    description: 'The Floor’s New PCs tray: every live boot PC with no station linked, newest first, with its graphics family, processor, ' +
        'network cards, when it was first seen and its last start. Requires `boot:view` and the `boot` add-on.',
    [PERMISSION_EXTENSION]: BOOT_VIEW,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(BootNewPcListSchema, 'The new PCs.'),
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        500: commonProblemResponses[500],
    },
});
export const IdentifyBootPcSchema = z
    .object({
    /** What the PC shows, large: the number it would be given ("14"). */
    label: z.string().trim().min(1).max(12),
})
    .openapi('IdentifyBootPc');
export const BootPcIdentifySchema = z
    .object({
    bootPcId: BootPcIdSchema,
    label: z.string(),
    /** It shows the label until this time (`BOOT_IDENTIFY_SECONDS` from now). */
    until: IsoDateTimeSchema,
})
    .openapi('BootPcIdentify', { description: 'An Identify sent to a boot PC through its boot server: what it shows, and until when.' });
export const identifyBootPcRoute = createRoute({
    method: 'post',
    path: `${BOOT_BASE}/pcs/{bootPcId}/identify`,
    operationId: 'identifyBootPc',
    tags: ['boot'],
    summary: 'Show a large number on a boot PC’s own screen (F-BOOT-25)',
    description: 'Sends `boot.pc.identify` to the venue’s boot server, which hands it to the PC: it shows `label` full screen for 60 seconds, ' +
        'so staff can tell which PC in the room it is. A PC that is off, or not yet in Windows, cannot show it. `409` when the ' +
        'boot server is not online. Requires `boot:manage`. Not audited: it changes nothing.',
    [PERMISSION_EXTENSION]: BOOT_MANAGE,
    request: {
        params: BootPcParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: IdentifyBootPcSchema } }, required: true },
    },
    responses: {
        202: jsonResponse(BootPcIdentifySchema, 'The Identify, sent to the boot server.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: pcNotFound,
        409: problemResponse('The boot server is not online, or the PC was removed.', 'conflict'),
        500: commonProblemResponses[500],
    },
});
export const MakeBootPcStationSchema = z
    .object({
    /** The station's number: unique within the venue (the console offers the next free one). */
    number: z.int().min(1).max(MAX_STATION_NUMBER),
    classId: StationClassIdSchema,
    zoneId: ZoneIdSchema.optional(),
    name: z.string().trim().min(1).max(60).optional(),
})
    .strict()
    .openapi('MakeBootPcStation');
export const MadeBootPcStationSchema = z
    .object({ station: StationSchema, pc: BootPcSchema })
    .openapi('MadeBootPcStation', { description: 'The station made from a new PC, and its boot PC now linked to it.' });
export const makeBootPcStationRoute = createRoute({
    method: 'post',
    path: `${BOOT_BASE}/pcs/{bootPcId}/make-station`,
    operationId: 'makeBootPcStation',
    tags: ['boot'],
    summary: 'Make a new PC a station on the Floor (F-BOOT-25)',
    description: 'One transaction: creates the station through the stations service (`client: station` where the venue has Clavian ' +
        'Stations, else `none`; audited `stations.station.create`), links the boot PC to it (audited as a PC assignment) and, ' +
        'when the Clavian Station app on that PC already asked to pair with a verified boot attestation for its current boot, ' +
        'pairs it with no code (audited `stations.station.paired_by_boot`). Requires `boot:manage` **and** `stations:manage`. ' +
        'Send an `Idempotency-Key`: a retry with it answers what the first made.',
    [PERMISSION_EXTENSION]: BOOT_MANAGE,
    request: {
        params: BootPcParamsSchema,
        headers: IdempotencyHeaderSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: MakeBootPcStationSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(MadeBootPcStationSchema, 'The station and its boot PC.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such boot PC, class or zone at this venue.', 'not-found'),
        409: problemResponse('The PC is already a station, it was removed, or another station has that number.', 'conflict'),
        422: problemResponse('The same Idempotency-Key was already used for a different request.', 'idempotency-key-reused'),
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Wipes (F-BOOT-06, F-BOOT-14)
// ---------------------------------------------------------------------------------------------
export const CreateBootWipeSchema = z
    .object({
    kind: BootWipeKindSchema,
    scope: BootWipeScopeSchema,
    /** `pc`: the PC. */
    bootPcId: BootPcIdSchema.optional(),
    /** `family` (shader caches only): the GPU family. */
    gpuFamily: BootGpuFamilySchema.optional(),
    /** Why: driver change, corruption… Audited. */
    reason: z.string().trim().min(1).max(200),
})
    .openapi('CreateBootWipe');
export const BootWipeSchema = z
    .object({
    kind: BootWipeKindSchema,
    scope: BootWipeScopeSchema,
    bootPcId: BootPcIdSchema.nullable(),
    gpuFamily: BootGpuFamilySchema.nullable(),
    reason: z.string(),
    /** A shader-cache wipe is kept until the boot server reports it done; a write-back wipe is not. */
    shaderWipeId: BootShaderWipeIdSchema.nullable(),
    requestedAt: IsoDateTimeSchema,
})
    .openapi('BootWipe', { description: 'A force-wipe sent to the boot server.' });
export const createBootWipeRoute = createRoute({
    method: 'post',
    path: `${BOOT_BASE}/wipes`,
    operationId: 'createBootWipe',
    tags: ['boot'],
    summary: 'Force-wipe write-back or shader caches (F-BOOT-06, F-BOOT-14)',
    description: 'Write-back: one PC or the fleet; the server drops it now (a PC that is running reboots onto a clean disk). Shader ' +
        'caches: one PC, one GPU family or the fleet; reset from the family’s seed. Requires `boot:wipe`. Audited with the reason.',
    [PERMISSION_EXTENSION]: BOOT_WIPE,
    request: { params: VenueScopeParamsSchema, body: { content: { [JSON_CONTENT_TYPE]: { schema: CreateBootWipeSchema } }, required: true } },
    responses: {
        202: jsonResponse(BootWipeSchema, 'The wipe, sent to the boot server.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: pcNotFound,
        409: problemResponse('The venue has no paired boot server.', 'conflict'),
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Shader-cache seed (F-BOOT-14)
// ---------------------------------------------------------------------------------------------
export const BootShaderSeedPromotionSchema = z
    .object({
    bootPcId: BootPcIdSchema,
    /** The GPU family whose seed the PC's volume becomes: the one the PC's last boot reported. */
    gpuFamily: BootGpuFamilySchema,
    requestedAt: IsoDateTimeSchema,
})
    .openapi('BootShaderSeedPromotion', { description: 'A request to make a PC’s shader cache its GPU family’s seed, sent to the boot server.' });
export const promoteBootShaderSeedRoute = createRoute({
    method: 'post',
    path: `${BOOT_BASE}/pcs/{bootPcId}/shader-seed`,
    operationId: 'promoteBootShaderSeed',
    tags: ['boot'],
    summary: 'Make a PC’s shader cache its GPU family’s seed (F-BOOT-14)',
    description: 'Sends `boot.shader.promote` to the venue’s boot server, which must be online. The PC must be shut down, and so must ' +
        'every PC whose shader cache was made from the family’s current seed; the server reports the result in ' +
        '`boot.job.progress` (job `shader_seed`). New PCs of the family start from the seed, and PCs made from the old one ' +
        'are reset from it at their next boot. Requires `boot:manage`. Audited.',
    [PERMISSION_EXTENSION]: BOOT_MANAGE,
    request: { params: BootPcParamsSchema },
    responses: {
        202: jsonResponse(BootShaderSeedPromotionSchema, 'The request, sent to the boot server.'),
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: pcNotFound,
        409: problemResponse('The PC has reported no GPU family, or the venue’s boot server is not online.', 'conflict'),
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Session-safe rollout (F-BOOT-12)
// ---------------------------------------------------------------------------------------------
export const RestartIdleBootPcsSchema = z
    .object({
    imageId: BootImageIdSchema,
    /** Only these PCs (the Floor's per-PC restart and its Update waiting filter); absent, every PC behind on the image. */
    bootPcIds: z.array(BootPcIdSchema).min(1).max(500).optional(),
})
    .openapi('RestartIdleBootPcs');
/** Why a behind PC was not restarted. */
export const BOOT_ROLLOUT_SKIP_REASONS = ['not_a_station', 'in_session', 'reserved', 'editing'];
export const BootRolloutSkipReasonSchema = z.enum(BOOT_ROLLOUT_SKIP_REASONS).openapi('BootRolloutSkipReason', {
    description: 'Why a PC on an older version was not restarted: it is not linked to a station, a session runs on it, it is reserved ' +
        'within the next 30 minutes, or it is in an edit boot.',
});
export const BootRolloutRestartedSchema = z
    .object({ bootPcId: BootPcIdSchema, stationId: StationIdSchema, commandId: StationCommandIdSchema })
    .openapi('BootRolloutRestarted', { description: 'A PC restarted onto the current version, and the power command that does it.' });
export const BootRolloutSkippedSchema = z
    .object({ bootPcId: BootPcIdSchema, stationId: StationIdSchema.nullable(), reason: BootRolloutSkipReasonSchema })
    .openapi('BootRolloutSkipped', { description: 'A PC on an older version that was left alone, and why.' });
export const BootRolloutRestartSchema = z
    .object({
    imageId: BootImageIdSchema,
    currentVersionNumber: z.int().min(1),
    restarted: z.array(BootRolloutRestartedSchema),
    skipped: z.array(BootRolloutSkippedSchema),
})
    .openapi('BootRolloutRestart', { description: 'The PCs restarted onto the image’s current version, and the ones left alone and why.' });
export const restartIdleBootPcsRoute = createRoute({
    method: 'post',
    path: `${BOOT_BASE}/rollouts/restart-idle`,
    operationId: 'restartIdleBootPcs',
    tags: ['boot'],
    summary: 'Restart idle PCs onto the current version (F-BOOT-12)',
    description: 'Restarts, through the station power command, the PCs on an older version of the image that are linked to a station, ' +
        'have no running session and no reservation in the next 30 minutes. A PC with a session is never restarted. ' +
        'Requires `boot:manage`. Audited.',
    [PERMISSION_EXTENSION]: BOOT_MANAGE,
    request: { params: VenueScopeParamsSchema, body: { content: { [JSON_CONTENT_TYPE]: { schema: RestartIdleBootPcsSchema } }, required: true } },
    responses: {
        202: jsonResponse(BootRolloutRestartSchema, 'What was restarted and what was not.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: imageNotFound,
        409: problemResponse('The image has no published version.', 'conflict'),
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Health (F-BOOT-20)
// ---------------------------------------------------------------------------------------------
export const BootMetricsQuerySchema = z.object({
    from: IsoDateTimeSchema,
    to: IsoDateTimeSchema,
    /** One PC; absent, every PC and the server's own row. */
    pc: BootPcIdSchema.optional(),
});
export const BootMetricPointSchema = z
    .object({
    /** The minute (or the UTC day) the point is for. */
    at: IsoDateTimeSchema,
    /** Null: the boot server's own row. */
    bootPcId: BootPcIdSchema.nullable(),
    readOps: z.int().min(0),
    p50Us: z.int().min(0),
    p99Us: z.int().min(0),
    /** Cache hits as a percentage, 0–100. */
    hitPct: z.number().min(0).max(100),
    bytes: z.int().min(0),
    /** The server's uplink use, 0–100; null on a PC's row. */
    uplinkPct: z.number().min(0).max(100).nullable(),
    /** The server's disk queue; null on a PC's row. */
    diskQueue: z.number().min(0).nullable(),
})
    .openapi('BootMetricPoint', { description: 'One minute (or day) of boot health for one PC, or for the server.' });
export const BootMetricsSchema = z
    .object({
    /** `minute` for a range of two days or less (14 days are kept); `day` beyond it (a year is kept). */
    granularity: z.enum(['minute', 'day']).openapi('BootMetricsGranularity'),
    points: z.array(BootMetricPointSchema),
})
    .openapi('BootMetrics', { description: 'Boot health over a range: per minute, or per day for a longer range.' });
export const getBootMetricsRoute = createRoute({
    method: 'get',
    path: `${BOOT_BASE}/metrics`,
    operationId: 'getBootMetrics',
    tags: ['boot'],
    summary: 'Boot health: per-PC read latency, cache hits, bytes; server uplink and disk queue (F-BOOT-20)',
    description: 'Per minute for a range up to two days, per day beyond. Requires `boot:view` and the `boot` add-on.',
    [PERMISSION_EXTENSION]: BOOT_VIEW,
    request: { params: VenueScopeParamsSchema, query: BootMetricsQuerySchema },
    responses: {
        200: jsonResponse(BootMetricsSchema, 'The points, oldest first.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        403: commonProblemResponses[403],
        404: problemResponse('No such venue or PC here.', 'not-found'),
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// The boot server's own routes (spec/25 §13.1–§13.2)
// ---------------------------------------------------------------------------------------------
export const PairBootServerRequestSchema = z
    .object({
    /** The one-time code a member made in the console (`POST …/boot/servers/pairing-code`). */
    code: z.string().trim().length(8),
    /** The server's Ed25519 public key, base64url of the raw 32 bytes. Its private half never leaves the server. */
    publicKey: z.string().min(1).max(128),
    /** The Clavian Boot service version. */
    version: z.string().min(1).max(64),
    /** The machine's name, for staff. */
    hostname: z.string().min(1).max(64).optional(),
})
    .openapi('PairBootServerRequest', { description: 'A boot server pairing itself with the code staff typed into its first-run screen.' });
export const PairedBootServerSchema = z
    .object({
    bootServerId: BootServerIdSchema,
    /** The device the server authenticates as (`POST /v1/device-auth/challenge`, `/token`), kind `boot_server`. */
    deviceId: DeviceIdSchema,
    organizationId: OrganizationIdSchema,
    venueId: VenueIdSchema,
    name: z.string(),
})
    .openapi('PairedBootServer', { description: 'The paired boot server: its identity and the device it authenticates as.' });
export const pairBootServerRoute = createRoute({
    method: 'post',
    path: '/v1/boot-servers/pair',
    operationId: 'pairBootServer',
    tags: ['boot'],
    summary: 'A boot server pairs itself with a one-time code (F-BOOT-01)',
    description: 'Public, authorised by the code and rate-limited to 10 per hour per IP. Binds the server’s device key to the venue the ' +
        'code was made for, as device kind `boot_server`; a key bound before is revoked. The server then authenticates with ' +
        '`POST /v1/device-auth/challenge` and `/token`. An unknown, used or expired code is one `404`.',
    request: { body: { content: { [JSON_CONTENT_TYPE]: { schema: PairBootServerRequestSchema } }, required: true } },
    responses: {
        201: jsonResponse(PairedBootServerSchema, 'The server is paired.'),
        400: commonProblemResponses[400],
        404: problemResponse('No such code, or it was used or has expired.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/**
 * The boot config as the boot server receives it (spec/25 §13.2): a **JWS with a detached payload** (RFC 7515
 * Appendix F). `payload` is the base64url of the exact UTF-8 bytes of a `BootConfig` JSON document; `signature` is the
 * compact JWS `<protected>..<signature>` over `<protected>.<payload>`, `alg: EdDSA` with the cloud's boot-config key,
 * whose `kid` the boot server pins. The server verifies the signature over the bytes it was sent, then parses them —
 * never re-serialises — and applies the config only when its `bootServerId` and `venueId` are its own.
 */
export const SignedBootConfigSchema = z
    .object({
    kid: z.string().min(1).max(64),
    payload: z.string().min(1),
    signature: z.string().min(1).max(4096),
})
    .openapi('SignedBootConfig', {
    description: 'The boot config, signed by the cloud’s boot-config key: a JWS with a detached, base64url-encoded payload.',
});
export const getBootServerConfigRoute = createRoute({
    method: 'get',
    path: '/v1/boot-server/config',
    operationId: 'getBootServerConfig',
    tags: ['boot'],
    summary: 'The boot server’s signed config and licence lease (F-BOOT-01, F-BOOT-16)',
    description: 'Boot-server-authenticated: `Authorization: Bearer <device token>` of a device of kind `boot_server`. A station’s, a ' +
        'member’s or a player’s token, an expired one and a revoked device are each `401`. The server fetches it at start, ' +
        'daily, and on `boot.config.updated`, and keeps the last good one on disk.',
    responses: {
        200: jsonResponse(SignedBootConfigSchema, 'The signed config.'),
        401: commonProblemResponses[401],
        402: ENTITLEMENT_REQUIRED,
        500: commonProblemResponses[500],
        503: problemResponse('This deployment has no boot-config signing key.', 'service-unavailable'),
    },
});
// ---------------------------------------------------------------------------------------------
// The signed config document (spec/25 §13.2–§13.3)
// ---------------------------------------------------------------------------------------------
/** The version of the `BootConfig` document's shape. A boot server refuses a version it does not know. */
export const BOOT_CONFIG_SCHEMA_VERSION = 1;
/** How long a licence lease lasts, and its grace after that (spec/25 §13.3, ADR-0063 decision 2). */
export const BOOT_LICENCE_LEASE_DAYS = 30;
export const BOOT_LICENCE_GRACE_DAYS = 14;
export const BootLicenceSchema = z
    .object({
    bootServerId: BootServerIdSchema,
    organizationId: OrganizationIdSchema,
    venueId: VenueIdSchema,
    /** How many PCs this server may boot until `validUntil` (the add-on's quantity). */
    maxBootPcs: z.int().min(0),
    issuedAt: IsoDateTimeSchema,
    /** `issuedAt` + 30 days. */
    validUntil: IsoDateTimeSchema,
    /** `validUntil` + 14 days: known PCs keep booting, new ones are refused. */
    graceUntil: IsoDateTimeSchema,
})
    .openapi('BootLicence', { description: 'The licence lease: how many PCs this boot server may boot, and until when (spec/25 §13.3).' });
/** The venue's Clavian Boot settings, resolved at the venue (spec/25 §14). Per-PC values are on each `BootConfigPc`. */
export const BootConfigSettingsSchema = z
    .object({
    dhcpMode: z.enum(['proxy', 'full']).openapi('BootDhcpMode'),
    autoAddPcs: z.boolean(),
    menu: z.enum(['off', 'on']).openapi('BootMenu'),
    writebackPolicy: BootWritebackPolicySchema,
    writebackQuotaGb: z.int().min(1),
    /** Null: the server's own default (60 % of RAM minus 4 GB). */
    ramCacheGb: z.int().min(1).nullable(),
    shaderCache: z.boolean(),
    shaderCacheGb: z.int().min(1),
    rolloutRestart: z.enum(['never', 'idle']).openapi('BootRolloutRestartMode'),
})
    .openapi('BootConfigSettings', { description: 'The venue’s Clavian Boot settings, resolved.' });
export const BootConfigVersionSchema = z
    .object({
    id: BootImageVersionIdSchema,
    number: z.int().min(1),
    parentVersionId: BootImageVersionIdSchema.nullable(),
    state: BootImageVersionStateSchema,
    /**
     * The image `parentVersionId` belongs to when it is another image's (an image saved from another, spec/25 §10): the
     * parent layer is in that image's folder. Null or absent: the parent is this image's own.
     */
    parentImageId: BootImageIdSchema.nullable().optional(),
})
    .openapi('BootConfigVersion', { description: 'One image version the boot server keeps: the chain a PC’s disk is built from.' });
export const BootConfigImageSchema = z
    .object({
    id: BootImageIdSchema,
    name: z.string(),
    kind: BootImageKindSchema,
    currentVersionId: BootImageVersionIdSchema.nullable(),
    defaultForVenue: z.boolean(),
    nicDriverMode: BootNicDriverModeSchema,
    nicFamilies: z.array(z.string()),
    /** A game disk's system images, or null for every one. */
    appliesToImageIds: z.array(BootImageIdSchema).nullable(),
    /**
     * A game disk's drive letter: a server edit mounts on it, and PCs give the game disk's volume this letter at boot.
     * Null for a system image. Optional: a config built before carries none (the boot server then uses `G`).
     */
    driveLetter: BootDriveLetterSchema.nullable().optional(),
    /** Every version still on disk (published, editing, preparing), oldest first. */
    versions: z.array(BootConfigVersionSchema),
    /** A merge to run, up to and including this version; null when none. */
    mergeUpToVersionId: BootImageVersionIdSchema.nullable(),
})
    .openapi('BootConfigImage', { description: 'An image or game disk the boot server serves, with its versions.' });
export const BootConfigPcSchema = z
    .object({
    id: BootPcIdSchema,
    mac: BootMacSchema,
    stationId: StationIdSchema.nullable(),
    /** The image it boots (its own, or the venue default), resolved; null when there is none. */
    imageId: BootImageIdSchema.nullable(),
    /** The version it boots: the image's current one, or the edit version during an edit boot. */
    versionId: BootImageVersionIdSchema.nullable(),
    /** The game disks it sees as LUN 1, resolved. */
    gameDiskIds: z.array(BootImageIdSchema),
    /** Its write-back policy, resolved: its own override, else its station's, else the venue's. */
    writebackPolicy: BootWritebackPolicySchema,
    /** Whether it gets a persistent shader-cache volume (LUN 2), resolved for its station. */
    shaderCache: z.boolean(),
    /** The edit boot it is in, if any. */
    editId: BootEditIdSchema.nullable(),
})
    .openapi('BootConfigPc', { description: 'A known boot PC and what it boots, every policy resolved for it.' });
export const BootConfigEditSchema = z
    .object({
    id: BootEditIdSchema,
    imageId: BootImageIdSchema,
    versionId: BootImageVersionIdSchema,
    parentVersionId: BootImageVersionIdSchema.nullable(),
    /** `pc`: an edit boot; `server`: a game disk attached on the boot server. Optional: absent is `pc` (a config built before). */
    target: BootEditTargetSchema.optional(),
    /** An edit boot's PC and its MAC; both null for a server edit. */
    bootPcId: BootPcIdSchema.nullable(),
    mac: BootMacSchema.nullable(),
    startedAt: IsoDateTimeSchema,
    /** Null while open; `published` or `discarded` once staff ended it and the server has not yet closed the layer. */
    outcome: BootEditOutcomeSchema.nullable(),
    note: z.string().nullable(),
    /**
     * Published as a new image (“Save as a new image”, spec/25 §10): the layer becomes version 1 of this image, in its own
     * folder, over the parent in this edit's image's folder. Null or absent: a new version of `imageId`, as before.
     */
    publishToImageId: BootImageIdSchema.nullable().optional(),
})
    .openapi('BootConfigEdit', { description: 'An edit (an edit boot, or a game disk’s server edit) the server has to keep open, or close.' });
export const BootConfigCaptureSchema = z
    .object({
    imageId: BootImageIdSchema,
    versionId: BootImageVersionIdSchema,
    mode: BootImageSourceSchema,
    /** SHA-256 (hex) of the one-time capture code: the server compares the reference PC's code against it. */
    captureCodeSha256: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
    expiresAt: IsoDateTimeSchema.nullable(),
    /** `import`: the file on the server to import; `folder`: the drive or folder whose files make the game disk. */
    importPath: z.string().nullable(),
    /** `folder`: copy the files, or move them (each deleted from the folder once its copy is checked). Absent is `copy`. */
    folderMode: BootFolderModeSchema.optional(),
})
    .openapi('BootConfigCapture', { description: 'A capture, import or game disk from a folder the server is waiting for.' });
export const BootConfigShaderWipeSchema = z
    .object({
    id: BootShaderWipeIdSchema,
    scope: BootWipeScopeSchema,
    /** `pc`: the MAC; `family`: the GPU family; `fleet`: null. */
    target: z.string().nullable(),
    requestedAt: IsoDateTimeSchema,
})
    .openapi('BootConfigShaderWipe', { description: 'A shader-cache wipe the server has not reported done.' });
export const BootConfigSchema = z
    .object({
    /** `BOOT_CONFIG_SCHEMA_VERSION` (1). A boot server refuses a version it does not know. */
    schemaVersion: z.int().min(1).max(1000),
    bootServerId: BootServerIdSchema,
    organizationId: OrganizationIdSchema,
    venueId: VenueIdSchema,
    /** When the cloud built it. A server refuses a config older than the last one it applied (clock rollback, §13.3). */
    issuedAt: IsoDateTimeSchema,
    licence: BootLicenceSchema,
    settings: BootConfigSettingsSchema,
    images: z.array(BootConfigImageSchema),
    pcs: z.array(BootConfigPcSchema),
    edits: z.array(BootConfigEditSchema),
    captures: z.array(BootConfigCaptureSchema),
    shaderWipes: z.array(BootConfigShaderWipeSchema),
})
    .openapi('BootConfig', {
    description: 'Everything a boot server needs to boot the venue’s PCs with the cloud unreachable: its licence lease, the venue’s ' +
        'settings, images and versions, PCs with their resolved policies, edits, pending captures and shader-cache wipes.',
});
// ---------------------------------------------------------------------------------------------
// The boot server's LAN API (spec/25 §13.4) — served by the boot server, not the cloud. Named here so the boot server
// and `Clavian.Boot.Client` share generated DTOs.
// ---------------------------------------------------------------------------------------------
/** What a PC shows when staff press Identify (spec/13 §3.9): a short label, until a time. */
export const BootLocalIdentifySchema = z
    .object({
    label: z.string().min(1).max(12),
    until: IsoDateTimeSchema,
})
    .openapi('BootLocalIdentify', { description: 'A label the PC shows large on its own screen until `until` (Identify).' });
/** `GET /local/v1/whoami`, answered by source IP/MAC. */
export const BootLocalWhoamiSchema = z
    .object({
    mac: BootMacSchema,
    stationId: StationIdSchema.nullable(),
    imageId: BootImageIdSchema.nullable(),
    versionNumber: z.int().min(1).nullable(),
    /** This boot writes into an unpublished version: the station client treats the PC as image maintenance. */
    editBoot: z.boolean(),
    writebackPolicy: BootWritebackPolicySchema,
    shaderCache: z.boolean(),
    /**
     * The drive letter of the game disk this boot serves as LUN 1 (spec/25 §12): the client gives its volume this letter.
     * Null when the PC has no game disk. Optional: a server built before sends none.
     */
    gameDiskDriveLetter: BootDriveLetterSchema.nullable().optional(),
    /**
     * Staff pressed Identify (spec/13 §3.9): while set, the PC shows `label` large, full screen and top-most, until
     * `until`. Null when nothing is to show. Optional: a server built before sends none.
     */
    identify: BootLocalIdentifySchema.nullable().optional(),
})
    .openapi('BootLocalWhoami', { description: 'A boot PC asking its boot server what this boot is (spec/25 §13.4).' });
/** The client cache driver's own numbers (spec/25 §8), as `Clavian.Boot.Client` reads them over its control device. */
export const BootClientCacheStatusSchema = z
    .object({
    /** The cache driver's hit rate since boot, 0–100. */
    hitPct: z.number().min(0).max(100),
    ramCacheBytes: z.int().min(0),
    scratchCacheBytes: z.int().min(0),
    /** Where this boot's writes go, as the driver applied it. */
    writebackLocation: BootWritebackLocationSchema,
})
    .openapi('BootClientCacheStatus', { description: 'The client cache driver’s hit rate, cache sizes and where this boot writes.' });
/** Whether the client gave the game disk's volume its drive letter this boot (spec/25 §12). */
export const BootClientGameDiskStatusSchema = z
    .object({
    /** The letter the boot server asked for. */
    driveLetter: BootDriveLetterSchema,
    /** The game disk's volume is on that letter now. */
    assigned: z.boolean(),
    /** What the client did or why it could not (the letter is another volume's, no game disk was found…). */
    detail: z.string().max(500).nullable(),
})
    .openapi('BootClientGameDiskStatus', { description: 'Whether the PC’s game disk volume got its drive letter this boot.' });
/** `POST /local/v1/client/status`: what `Clavian.Boot.Client` reports after a boot. */
export const BootLocalClientStatusSchema = z
    .object({
    clientVersion: z.string().min(1).max(64),
    cache: BootClientCacheStatusSchema,
    /** A usable local NVMe scratch partition is present (the `auto` policy's input). */
    scratchPresent: z.boolean(),
    gpuFamily: BootGpuFamilySchema.nullable(),
    nicPciIds: z.array(BootPciIdSchema).max(16),
    /** Whether UEFI Secure Boot is on; null when the client could not read it. */
    secureBoot: z.boolean().nullable(),
    /** Seconds from the PXE request to the station's lock screen, when known. */
    bootSeconds: z.int().min(0).nullable(),
    /**
     * The shader-cache volume cannot serve this PC (it was built for another GPU family, or it is unreadable): the
     * server resets it when the PC's disks next close, so its next boot gets a fresh one from the family's seed.
     */
    shaderCacheResetRequested: z.boolean(),
    /** The game disk's drive letter this boot; null without a game disk. Optional: a client built before sends none. */
    gameDisk: BootClientGameDiskStatusSchema.nullable().optional(),
    /** The processor's name as Windows reports it, for the Floor's New PCs tray. Optional: a client built before sends none. */
    cpu: BootCpuNameSchema.nullable().optional(),
    /** The PC's motherboard (SMBIOS baseboard manufacturer and product). Optional: a client built before sends none. */
    motherboard: BootMotherboardSchema.nullable().optional(),
})
    .openapi('BootLocalClientStatus', { description: 'A boot PC’s report to its boot server: cache, scratch, GPU, NICs, Secure Boot.' });
// ---------------------------------------------------------------------------------------------
// The boot attestation (spec/10 §14.2, spec/25 §13.4; ADR-0065 decision 4, F-BOOT-26)
// ---------------------------------------------------------------------------------------------
/** The `aud` of a boot attestation: it is accepted by the station pairing start and nowhere else. */
export const BOOT_PAIRING_ATTESTATION_AUDIENCE = 'clavian-station-pairing';
/** The JWS `typ` of a boot attestation's protected header. */
export const BOOT_PAIRING_ATTESTATION_TYP = 'clavian-boot-attestation+jws';
/** How long an attestation lives: `exp` is `iat` + 120. */
export const BOOT_PAIRING_ATTESTATION_TTL_SECONDS = 120;
/** The attestation's protected header: `{ alg: EdDSA, kid: <the boot server's device id>, typ }`. */
export const BootPairingAttestationHeaderSchema = z
    .object({
    alg: z.literal('EdDSA'),
    /** The boot server's device (`dev_…`, kind `boot_server`): the key it was paired with signs. */
    kid: DeviceIdSchema,
    typ: z.literal(BOOT_PAIRING_ATTESTATION_TYP),
})
    .openapi('BootPairingAttestationHeader', { description: 'The protected header of a boot attestation (compact JWS, EdDSA).' });
/**
 * The payload of a boot attestation: the boot server vouching that the PC asking is the boot PC with this MAC, in this
 * boot, for one pairing attempt (the nonce) by one Station app key (its fingerprint). Signed with the boot server's device
 * key as a compact JWS; the cloud verifies over the bytes sent, then parses (spec/10 §14.2).
 */
export const BootPairingAttestationSchema = z
    .object({
    /** Format version: 1. */
    v: z.int().min(1).max(1),
    aud: z.literal(BOOT_PAIRING_ATTESTATION_AUDIENCE),
    bootServerId: BootServerIdSchema,
    venueId: VenueIdSchema,
    organizationId: OrganizationIdSchema,
    /** The boot NIC's MAC as the boot server saw it in the PC's iSCSI session: lower-case, colon-separated. */
    mac: z.string().regex(/^([0-9a-f]{2}:){5}[0-9a-f]{2}$/),
    bootId: BootIdSchema,
    /** The Station app's nonce for this attempt: 16 random bytes, base64url without padding. */
    nonce: z.string().regex(/^[A-Za-z0-9_-]{22}$/),
    /** base64url (no padding) SHA-256 of the Station app's raw 32-byte Ed25519 public key. */
    keyFingerprint: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    /** Seconds since the epoch. */
    iat: z.int().min(0),
    /** `iat` + 120. */
    exp: z.int().min(0),
})
    .openapi('BootPairingAttestation', { description: 'A boot server vouching for one PC’s boot, for one Station app pairing attempt.' });
/**
 * `POST /local/v1/pairing-attestation`, asked by `Clavian.Boot.Client` for the Station app — and the request the Station app
 * sends `Clavian.Boot.Client` over the local pipe `\\.\pipe\ClavianBoot.Attestation` (SYSTEM only), the same shape.
 */
export const BootLocalPairingAttestationRequestSchema = z
    .object({
    nonce: z.string().regex(/^[A-Za-z0-9_-]{22}$/),
    stationKeyFingerprint: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
})
    .openapi('BootLocalPairingAttestationRequest', { description: 'Asking the boot server to vouch for this PC’s boot, for one pairing attempt.' });
/** The answer: the compact JWS, and when it expires. The pipe answers the same. */
export const BootLocalPairingAttestationSchema = z
    .object({
    attestation: z.string().min(16).max(4096),
    expiresAt: IsoDateTimeSchema,
})
    .openapi('BootLocalPairingAttestation', { description: 'A signed boot attestation for this PC’s boot (valid two minutes, used once).' });
/**
 * The local named pipes on a Clavian-booted PC (spec/10 §14.2): `ClavianBoot.Attestation` — served by
 * `Clavian.Boot.Client`, ACL SYSTEM only; one request line of `BootLocalPairingAttestationRequest` JSON, one answer line of
 * `BootLocalPairingAttestationPipeAnswer` JSON, then it closes.
 */
export const BOOT_LOCAL_PIPES = ['ClavianBoot.Attestation'];
export const BootLocalPipeSchema = z.enum(BOOT_LOCAL_PIPES).openapi('BootLocalPipe', {
    description: 'A local named pipe on a Clavian-booted PC (`\\\\.\\pipe\\<name>`).',
});
/** Why the pipe could not give an attestation (the Station app then pairs by code). */
export const BOOT_LOCAL_ATTESTATION_FAILURES = ['no_boot_server', 'not_found', 'not_paired', 'rate_limited', 'invalid_request', 'error'];
export const BootLocalAttestationFailureSchema = z.enum(BOOT_LOCAL_ATTESTATION_FAILURES).openapi('BootLocalAttestationFailure', {
    description: 'Why no attestation came back: no boot server, the boot server does not know this boot, it is not paired, too many asks, a bad request, or an error.',
});
/** The pipe's one answer line: the attestation, or why there is none. */
export const BootLocalPairingAttestationPipeAnswerSchema = z
    .object({
    attestation: BootLocalPairingAttestationSchema.nullable(),
    failure: BootLocalAttestationFailureSchema.optional(),
})
    .openapi('BootLocalPairingAttestationPipeAnswer', { description: 'The answer on the attestation pipe: an attestation, or why there is none.' });
/** The size of one capture extent (spec/25 §9.1): 4 MiB. */
export const BOOT_CAPTURE_EXTENT_BYTES = 4 * 1024 * 1024;
/** One volume a reference PC captures. */
export const BootCaptureVolumeSchema = z
    .object({
    /** The volume's GUID path or letter on the reference PC. */
    volume: z.string().min(1).max(128),
    sizeBytes: z.int().min(0),
    usedBytes: z.int().min(0),
    clusterBytes: z.int().min(512),
})
    .openapi('BootCaptureVolume', { description: 'One volume of a reference PC being captured: its size, used space and cluster size.' });
/** One extent of a capture, by volume and offset. */
export const BootCaptureExtentRefSchema = z
    .object({ volume: z.string(), offset: z.int().min(0) })
    .openapi('BootCaptureExtentRef', { description: 'One captured extent, by volume and byte offset.' });
/** `POST /local/v1/captures`: the reference PC starts (or resumes) a capture with its one-time code. */
export const BootCaptureStartRequestSchema = z
    .object({
    captureCode: z.string().trim().min(6).max(16),
    osBuild: z.string().min(1).max(64),
    volumes: z.array(BootCaptureVolumeSchema).min(1).max(8),
    /** The reference PC's NICs: NIC driver mode `auto` counts them among the venue's hardware (spec/25 §9.3). */
    nicPciIds: z.array(BootPciIdSchema).max(16),
    /** The reference PC's motherboard (SMBIOS baseboard), recorded on the version (ADR-0065 decision 8). Optional. */
    motherboard: BootMotherboardSchema.nullable().optional(),
})
    .openapi('BootCaptureStartRequest', { description: 'A reference PC starting or resuming a capture (spec/25 §9.1).' });
export const BootCaptureSessionSchema = z
    .object({
    captureId: z.string().min(1).max(64),
    imageId: BootImageIdSchema,
    versionId: BootImageVersionIdSchema,
    /** `BOOT_CAPTURE_EXTENT_BYTES`: 4 MiB. */
    extentBytes: z.int().min(1).max(BOOT_CAPTURE_EXTENT_BYTES),
    /** Extents the server already holds, by volume and offset: a resumed capture skips them. */
    received: z.array(BootCaptureExtentRefSchema),
})
    .openapi('BootCaptureSession', { description: 'A capture the boot server accepted, and what it already has.' });
/**
 * The answer to `PUT /local/v1/captures/{captureId}/extents` (body: the extent's bytes; headers name the volume, the
 * offset and the SHA-256). An extent whose hash does not match is refused, and the PC sends it again.
 */
export const BootCaptureExtentReceiptSchema = z
    .object({
    volume: z.string(),
    offset: z.int().min(0),
    length: z.int().min(1).max(BOOT_CAPTURE_EXTENT_BYTES),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    accepted: z.boolean(),
})
    .openapi('BootCaptureExtentReceipt', { description: 'Whether the boot server kept one captured extent.' });
/**
 * The LAN API's paths (spec/25 §13.4), plain HTTP on TCP 8070: the boot server maps exactly these and
 * `Clavian.Boot.Client` calls exactly these.
 *
 *   GET  /local/v1/whoami                              → 200 BootLocalWhoami (the PC by its address or `?mac=`)
 *   POST /local/v1/client/status                       BootLocalClientStatus → 204 (the PC by its address only)
 *   GET  /local/v1/anticheat-filters                   → 200 BootAntiCheatFilterList
 *   POST /local/v1/captures                            BootCaptureStartRequest → 200 BootCaptureSession
 *   PUT  /local/v1/captures/{captureId}/extents        the extent's bytes → 200 BootCaptureExtentReceipt, or 422 with
 *                                                      the receipt (`accepted: false`) when the SHA-256 does not match
 *   POST /local/v1/captures/{captureId}/complete       → 202; preparation follows (`boot.job.progress`)
 *   POST /local/v1/pairing-attestation                 BootLocalPairingAttestationRequest → 200 BootLocalPairingAttestation
 *                                                      (the PC by the iSCSI session from its address only; 404 no live
 *                                                      session, 503 not paired, 429 over 10 a minute per PC)
 */
export const BOOT_LOCAL_PATHS = [
    '/local/v1/whoami',
    '/local/v1/client/status',
    '/local/v1/anticheat-filters',
    '/local/v1/captures',
    '/local/v1/captures/{captureId}/extents',
    '/local/v1/captures/{captureId}/complete',
    '/local/v1/pairing-attestation',
];
export const BootLocalPathSchema = z.enum(BOOT_LOCAL_PATHS).openapi('BootLocalPath', {
    description: 'A path of the boot server’s LAN API (TCP 8070), which the boot server maps and Clavian.Boot.Client calls.',
});
/**
 * The headers of the capture endpoints after the start: `Authorization: Bearer <capture code>` (the code as typed,
 * trimmed and upper-cased by the server before hashing) on the extents and the completion; and on each extent its
 * volume's name as in the start request, its byte offset in the volume (sector-aligned, decimal) and its SHA-256
 * (lower-case hex).
 */
export const BOOT_CAPTURE_HEADERS = ['Authorization', 'X-Clavian-Capture-Volume', 'X-Clavian-Capture-Offset', 'X-Clavian-Capture-Sha256'];
export const BootCaptureHeaderSchema = z.enum(BOOT_CAPTURE_HEADERS).openapi('BootCaptureHeader', {
    description: 'A header of the capture endpoints: the capture code as a bearer token, and an extent’s volume, offset and SHA-256.',
});
/** The scheme of the capture endpoints' `Authorization` header: `Bearer <capture code>`. */
export const BOOT_CAPTURE_AUTH_SCHEME = 'Bearer';
/**
 * Why the LAN API refused a request: the `code` of its problem document (`application/problem+json`, `type`
 * `about:blank`), each with one HTTP status —
 *   invalid_request 400 · capture_code_unknown 401 (no code, or one this server was not told about) · not_found 404 (no
 *   such capture, or a PC not booted from this server) · capture_conflict 409 (the capture is complete, under way with
 *   other volumes, or not finished) · capture_code_expired 410 (an expired code that started no capture) · rate_limited
 *   429 (too many attestations asked for one PC) · not_paired 503 (the boot server is not paired, so it cannot vouch).
 */
export const BOOT_LOCAL_PROBLEM_CODES = [
    'invalid_request',
    'capture_code_unknown',
    'not_found',
    'capture_conflict',
    'capture_code_expired',
    'rate_limited',
    'not_paired',
];
export const BootLocalProblemCodeSchema = z.enum(BOOT_LOCAL_PROBLEM_CODES).openapi('BootLocalProblemCode', {
    description: 'Why the boot server’s LAN API refused a request: 400, 401, 404, 409, 410, 429 or 503.',
});
export const BootLocalProblemSchema = z
    .object({
    type: z.literal('about:blank'),
    status: z.int().min(400).max(599),
    code: BootLocalProblemCodeSchema,
    detail: z.string().max(500),
})
    .openapi('BootLocalProblem', { description: 'A refusal from the boot server’s LAN API.' });
/** How one anti-cheat network filter must be set at one moment (spec/25 §9.4). */
export const BootAntiCheatFilterStateSchema = z
    .object({
    /** Bound to the network adapters. */
    bound: z.boolean(),
    /** The filter driver's service start type (0 boot … 4 disabled); null leaves it as installed. */
    serviceStart: z.int().min(0).max(4).nullable(),
})
    .openapi('BootAntiCheatFilterState', { description: 'Whether an anti-cheat network filter is bound, and its service start type.' });
export const BootAntiCheatFilterSchema = z
    .object({
    /** A short id: `faceit`. */
    id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,47}$/),
    name: z.string().min(1).max(128),
    vendor: z.string().min(1).max(128),
    /** The network binding's component id (`Get-NetAdapterBinding -ComponentID`): `FI_Lwf`. */
    componentId: z.string().regex(/^[A-Za-z0-9_.-]{1,64}$/),
    /** The filter driver's service key name. */
    service: z.string().regex(/^[A-Za-z0-9_.-]{1,64}$/),
    /** False until the names were checked on a PC with the anti-cheat installed. */
    verified: z.boolean(),
    /** How it must be set while a reference PC is captured: `bound: false` is unbound for the snapshot, then rebound. */
    capture: BootAntiCheatFilterStateSchema,
    /** How it must be set for play: preparation restores the service start type. */
    play: BootAntiCheatFilterStateSchema,
    notes: z.string().max(500).nullable(),
})
    .openapi('BootAntiCheatFilter', { description: 'A known anti-cheat network filter and how it must be set for capture and for play.' });
/**
 * `GET /local/v1/anticheat-filters` and the boot server's own data file `boot/Server/AntiCheat/anticheat-filters.json`
 * (spec/25 §9.4): the anti-cheat network filters a capture unbinds and preparation accounts for. One format for both.
 */
export const BootAntiCheatFilterListSchema = z
    .object({
    /** 1. A reader refuses a version it does not know. */
    schemaVersion: z.int().min(1).max(1000),
    filters: z.array(BootAntiCheatFilterSchema).max(64),
})
    .openapi('BootAntiCheatFilterList', { description: 'The anti-cheat network filters a boot server knows (spec/25 §9.4).' });
/** The named components only the boot server uses (no cloud route references them); registered by the document. */
export const BOOT_SERVER_COMPONENT_SCHEMAS = {
    BootConfig: BootConfigSchema,
    BootLicence: BootLicenceSchema,
    BootLocalPath: BootLocalPathSchema,
    BootLocalProblem: BootLocalProblemSchema,
    BootLocalWhoami: BootLocalWhoamiSchema,
    BootLocalClientStatus: BootLocalClientStatusSchema,
    BootLocalIdentify: BootLocalIdentifySchema,
    BootPairingAttestation: BootPairingAttestationSchema,
    BootPairingAttestationHeader: BootPairingAttestationHeaderSchema,
    BootLocalPairingAttestationRequest: BootLocalPairingAttestationRequestSchema,
    BootLocalPairingAttestation: BootLocalPairingAttestationSchema,
    BootLocalPairingAttestationPipeAnswer: BootLocalPairingAttestationPipeAnswerSchema,
    BootLocalPipe: BootLocalPipeSchema,
    BootAntiCheatFilterList: BootAntiCheatFilterListSchema,
    BootCaptureHeader: BootCaptureHeaderSchema,
    BootCaptureStartRequest: BootCaptureStartRequestSchema,
    BootCaptureSession: BootCaptureSessionSchema,
    BootCaptureExtentReceipt: BootCaptureExtentReceiptSchema,
};
/** Every Clavian Boot route, in the order the document lists them. */
export const BOOT_ROUTES = [
    listBootServersRoute,
    createBootServerPairingCodeRoute,
    updateBootServerRoute,
    unpairBootServerRoute,
    updateBootServerSoftwareRoute,
    listBootImagesRoute,
    createBootImageRoute,
    updateBootImageRoute,
    listBootImageVersionsRoute,
    restoreBootImageRoute,
    mergeBootImageRoute,
    prepareBootImageVersionRoute,
    makeBootImageIndependentRoute,
    startBootEditRoute,
    startBootServerEditRoute,
    publishBootEditRoute,
    discardBootEditRoute,
    listBootPcsRoute,
    getBootFloorRoute,
    getBootFloorStatesRoute,
    listBootClassesRoute,
    updateBootClassRoute,
    listBootNewPcsRoute,
    addBootPcRoute,
    updateBootPcRoute,
    removeBootPcRoute,
    identifyBootPcRoute,
    makeBootPcStationRoute,
    createBootWipeRoute,
    promoteBootShaderSeedRoute,
    restartIdleBootPcsRoute,
    getBootMetricsRoute,
    pairBootServerRoute,
    getBootServerConfigRoute,
];
//# sourceMappingURL=boot.js.map